import type {
  AIAgentMode,
  AIConfirmationProfile,
  AIDraftService,
  AIToolsConfigService,
} from '@affine/core/modules/ai-button';
import type {
  AIModelService,
  ReasoningModeId,
} from '@affine/core/modules/ai-button/services/models';
import {
  type ServerService,
  type SubscriptionService,
  workspaceApiFetch,
} from '@affine/core/modules/cloud';
import {
  confirmationEventBus,
  type ConfirmationEventDetail,
} from '@affine/core/modules/cloud/services/eventsource';
import type { WorkspaceDialogService } from '@affine/core/modules/dialogs';
import type { FeatureFlagService } from '@affine/core/modules/feature-flag';
import { forwardCopilotWorkflowStreamObject } from '@affine/core/modules/streamlain/workflow-viewer/copilot-workflow-live-update';
import { type CopilotChatHistoryFragment, SearchMode } from '@affine/graphql';
import { I18n } from '@affine/i18n';
import { menu, popMenu } from '@blocksuite/affine/components/context-menu';
import { SignalWatcher, WithDisposable } from '@blocksuite/affine/global/lit';
import { unsafeCSSVar, unsafeCSSVarV2 } from '@blocksuite/affine/shared/theme';
import type { EditorHost } from '@blocksuite/affine/std';
import { ShadowlessElement } from '@blocksuite/affine/std';
// E010-T015: Import for Edgeless viewport
import { GfxControllerIdentifier } from '@blocksuite/affine/std/gfx';
import type { NotificationService } from '@blocksuite/affine-shared/services';
import {
  ArrowUpBigIcon,
  CloseIcon,
  InformationIcon,
  NoNetworkIcon,
  StopIcon,
  ThinkingIcon,
  WebIcon,
} from '@blocksuite/icons/lit';
import type { GoalTreeEnvelope } from '@streamlain/view-config';
import { css, html, nothing, type PropertyValues, svg } from 'lit';
import { property, query, state } from 'lit/decorators.js';
import { repeat } from 'lit/directives/repeat.js';
import * as lz from 'lz-string'; // sl-57s: For decompressing clipboard snapshots

import { ChatAbortIcon } from '../../_common/icons';
import { resolveLeadingChatAgentMention } from '../../chat-panel/agent-mention';
import type { BotOptionData } from '../../chat-panel/ai-title';
import type { ConfirmationMessage } from '../../chat-panel/message/confirmation';
import { type AIError, AIProvider, type AISendParams } from '../../provider';
import {
  aiChatBadgeTypography,
  aiChatBodyTypography,
  aiChatControlTypography,
  aiChatIconSize,
  aiChatMetaTypography,
  aiChatStatusTypography,
} from '../../styles/ai-chat-typography';
import { reportResponse } from '../../utils/action-reporter';
import { finiteBoundsFromXYWH } from '../../utils/canvas-bounds';
import {
  getAIChatOnlineStatus,
  subscribeAIChatOnlineStatus,
} from '../../utils/chat-connectivity';
import { fetchChatKeysCheck } from '../../utils/chat-keys-check';
import {
  extractCanvasSelectionContext,
  extractSelectedContent,
  filterCanvasSelectionsByCoveredBlockIds,
} from '../../utils/extract';
import { readBlobAsURL } from '../../utils/image';
import type { MailMessageAttachment } from '../../utils/mail-selection-context';
import {
  isReasoningModeActive,
  resolveReasoningMode,
  resolveReasoningModel,
} from '../../utils/reasoning-mode';
import { mergeStreamObjects } from '../../utils/stream-objects';
import type { SearchMenuConfig } from '../ai-chat-add-context';
import {
  attachmentContextRefsFromChips,
  buildCanonicalMessageContext,
  fileContextAttachmentsFromChips,
} from '../ai-chat-chips/canonical-message-context';
import { getChatInputPortalReferenceElement } from '../ai-chat-chips/chat-input-portal-style';
import type { ChatChip, DocDisplayConfig } from '../ai-chat-chips/type';
import {
  isAttachmentChip,
  isCollectionChip,
  isDocChip,
  isFileChip,
  isSelectedContextChip,
  isTagChip,
} from '../ai-chat-chips/utils';
import type {
  AutomationAttachment,
  CanvasAIChatContextMode,
  CodeSelectionAttachment,
  DatabaseAttachment,
  DatabaseColumn,
  ExternalChatContextDoc,
  PdfAttachment,
  PdfContextAttachment,
  WorkflowAttachment,
} from '../ai-chat-content/type';
import {
  type ChatMessage,
  type HistoryMessage,
  isChatMessage,
  StreamObjectSchema,
  syncMissingMessageIds,
  type ToolMessageData,
} from '../ai-chat-messages';
import type { GoalPlanBlocker } from '../goal-tree/goal-tree-context';
import {
  configureStreamLainDropdownMenu,
  popupTargetFromBodyElement,
  streamLainDropdownMenuBoundaryWidthCss,
  type StreamLainDropdownMenuHandler,
  streamLainDropdownMenuMiddleware,
} from './dropdown-menu-chrome';
import {
  type GoalConfirmationPermission,
  persistGoalConfirmationRequest,
  persistGoalConfirmationResponse,
  persistTargetModePermissionRequest,
  persistTargetModePermissionResponse,
  restoreTargetModePermissionProposal,
  type TargetModePermissionProposal,
} from './goal-mode-permission-history';
import {
  resolveCopilotRetrievalPlan,
  resolveTargetModeProposal,
  setWebSearchToggleMode,
  shouldProposeAgentMode,
  type WebSearchToggleMode,
  webSearchToggleModeForRetrievalMode,
} from './retrieval-mode';
import type {
  AIChatInputContext,
  AINetworkSearchConfig,
  AIReasoningConfig,
} from './type';

const STREAMLAIN_CHAT_ICON_STROKE_WIDTH = 1.5;
const MODE_MENU_WIDTH = '220px';

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function readMessageType(message: ChatMessage): string | undefined {
  return (message as ChatMessage & { messageType?: string }).messageType;
}

function readToolData(message: ChatMessage): ToolMessageData | undefined {
  return isRecord(message.toolData)
    ? (message.toolData as ToolMessageData)
    : undefined;
}

function buildConfirmationFromApprovalMessage(
  message: ChatMessage
): ConfirmationMessage | null {
  if (readMessageType(message) !== 'tool_approval_request') return null;
  const toolData = readToolData(message);
  const approvalId = toolData?.approvalId;
  if (!approvalId) return null;
  const toolName = toolData.toolCall?.name ?? 'tool_confirmation';
  const toolArguments = isRecord(toolData.toolCall?.arguments)
    ? toolData.toolCall.arguments
    : {};
  return {
    type: 'confirmation',
    id: approvalId,
    toolName,
    displayName:
      toolName === 'target_mode' || toolName === 'agent_mode'
        ? I18n.t('com.streamlain.ai.target.label')
        : toolName,
    description:
      toolData.description ?? I18n.t('com.streamlain.ai.confirmation.title'),
    copy: toolData.confirmationCopy,
    settingsHint: null,
    ...(toolName === 'target_mode' || toolName === 'agent_mode'
      ? {
          metadata: {
            domain: toolArguments.domain,
            input: toolArguments.input,
            confirmationProfile: toolArguments.confirmationProfile,
            includesAgentMode: toolArguments.includesAgentMode,
            includesTargetMode: toolArguments.includesTargetMode,
          },
        }
      : {}),
  };
}

function hasApprovalFollowup(
  messages: readonly ChatMessage[],
  requestIndex: number,
  approvalId: string
): boolean {
  return messages.slice(requestIndex + 1).some(message => {
    const messageType = readMessageType(message);
    const toolData = readToolData(message);
    if (messageType === 'tool_approval_response') {
      return !toolData?.approvalId || toolData.approvalId === approvalId;
    }
    return messageType === 'tool_result';
  });
}

function findLatestPersistedPendingConfirmation(
  messages: readonly HistoryMessage[],
  ignoredConfirmationIds: readonly string[]
): ConfirmationMessage | null {
  const ignored = new Set(ignoredConfirmationIds);
  for (let index = messages.length - 1; index >= 0; index--) {
    const message = messages[index];
    if (!isChatMessage(message)) continue;
    const confirmation = buildConfirmationFromApprovalMessage(message);
    if (!confirmation || ignored.has(confirmation.id)) continue;
    const chatMessages = messages.filter(isChatMessage);
    const chatIndex = chatMessages.indexOf(message);
    if (hasApprovalFollowup(chatMessages, chatIndex, confirmation.id)) {
      continue;
    }
    return confirmation;
  }
  return null;
}

function canvasSelectionKey(
  selection: AIChatInputContext['canvasSelections'][number]
) {
  return [
    selection.docId,
    ...[...selection.elementIds].sort(),
    ...[...selection.blockIds].sort(),
  ].join(':');
}

function mergeCanvasSelections(
  explicitSelections: AIChatInputContext['canvasSelections'],
  implicitSelections: AIChatInputContext['canvasSelections']
) {
  const seen = new Set<string>();
  const merged: AIChatInputContext['canvasSelections'] = [];
  for (const selection of [...explicitSelections, ...implicitSelections]) {
    const key = canvasSelectionKey(selection);
    if (seen.has(key)) continue;
    seen.add(key);
    merged.push(selection);
  }
  return merged;
}

function dataUrlToFile(dataUrl: string, fileName: string, type: string): File {
  const binary = atob(dataUrl.split(',')[1] ?? '');
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return new File([bytes], fileName, { type });
}

// E020-S007: Microphone icon for STT
const MicrophoneIcon = () => svg`
  <svg width="1em" height="1em" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="${STREAMLAIN_CHAT_ICON_STROKE_WIDTH}" stroke-linecap="round" stroke-linejoin="round">
    <path d="M12 3.25a3 3 0 0 0-3 3v5.5a3 3 0 0 0 6 0v-5.5a3 3 0 0 0-3-3Z"/>
    <path d="M18.5 10.5v1.25a6.5 6.5 0 0 1-13 0V10.5"/>
    <path d="M12 18.25v3"/>
  </svg>
`;

const StopHandIcon = () => svg`
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 24 24"
    width="1em"
    height="1em"
    fill="none"
    stroke="currentColor"
    stroke-width="${STREAMLAIN_CHAT_ICON_STROKE_WIDTH}"
    stroke-linecap="round"
    stroke-linejoin="round"
    style="user-select:none;flex-shrink:0;"
  >
    <path d="M18 11V6a2 2 0 0 0-4 0v5" />
    <path d="M14 10V4a2 2 0 0 0-4 0v6.5" />
    <path d="M10 10V5a2 2 0 0 0-4 0v9" />
    <path d="M18 8a2 2 0 1 1 4 0v5a9 9 0 0 1-9 9h-1c-3.3 0-5.2-1-6.7-3L3 16a2 2 0 0 1 3.2-2.4L8 16" />
  </svg>
`;

// E020-S007: Recording constants
const MAX_RECORDING_SECONDS = 600;
const MIN_TRANSCRIPTION_RECORDING_MS = 300;

function getFirstTwoLines(text: string) {
  const lines = text.split('\n');
  return lines.slice(0, 2);
}

function webSearchModeLabel(mode: WebSearchToggleMode) {
  if (mode === 'off') {
    return I18n.t('com.streamlain.ai.chat.input.web-search.off');
  }
  return I18n.t('com.streamlain.ai.chat.input.web-search.auto');
}

function agentModeLabel(mode: AIAgentMode) {
  return mode === 'agent'
    ? I18n.t('com.streamlain.ai.chat.input.agent-mode.agent')
    : I18n.t('com.streamlain.ai.chat.input.agent-mode.chat');
}

function modeMenuLabel(label: string, description?: string) {
  return () =>
    html`<span
      style="display:flex;box-sizing:border-box;width:100%;min-width:0;align-items:center;gap:8px"
    >
      <span
        style="min-width:0;flex:0 1 auto;overflow:hidden;text-overflow:ellipsis;white-space:nowrap"
        >${label}</span
      >
      ${description
        ? html`<span
            role="button"
            tabindex="0"
            aria-label=${description}
            style="position:relative;display:inline-flex;width:20px;height:20px;flex:0 0 20px;align-items:center;justify-content:center;color:var(--streamlain-text-secondary-color);cursor:help"
            @click=${(event: MouseEvent) => event.stopPropagation()}
            @keydown=${(event: KeyboardEvent) => {
              if (event.key === 'Enter' || event.key === ' ') {
                event.stopPropagation();
              }
            }}
          >
            ${InformationIcon({ width: '16px', height: '16px' })}
            <affine-tooltip
              .autoFlip=${false}
              .autoShift=${true}
              .zIndex=${'calc(var(--streamlain-z-index-dropdown-panel, 10020) + 1)'}
              tip-position="top"
            >
              ${description}
            </affine-tooltip>
          </span>`
        : nothing}
    </span>`;
}

export class AIChatInput extends SignalWatcher(
  WithDisposable(ShadowlessElement)
) {
  static override styles = css`
    :host {
      width: 100%;
    }

    [data-theme='dark'] .chat-panel-input {
      box-shadow:
        var(--border-shadow),
        0px 0px 0px 0px rgba(28, 158, 228, 0),
        0px 0px 0px 2px transparent;
    }
    [data-theme='light'] .chat-panel-input,
    .chat-panel-input {
      box-shadow:
        var(--border-shadow),
        0px 0px 0px 3px transparent,
        0px 2px 3px rgba(0, 0, 0, 0.05);
    }
    .chat-panel-input[data-if-focused='true'] {
      box-shadow:
        var(--border-shadow),
        0px 0px 0px 3px transparent,
        0px 4px 6px rgba(0, 0, 0, 0.05);
    }
    [data-theme='dark'] .chat-panel-input[data-if-focused='true'] {
      box-shadow:
        var(--border-shadow),
        0px 0px 0px 3px rgba(28, 158, 228, 0.3),
        0px 2px 3px rgba(0, 0, 0, 0.05);
    }

    .chat-panel-input {
      --input-border-width: 0.5px;
      --input-border-color: var(--streamlain-v2-layer-insideBorder-border);
      --border-shadow: 0px 0px 0px var(--input-border-width)
        var(--input-border-color);
      --chat-input-surface-base: var(
        --streamlain-v2-layer-background-appChrome,
        var(
          --streamlain-background-primary-color,
          var(--affine-background-primary-color, #141414)
        )
      );
      --chat-input-surface-background: color-mix(
        in srgb,
        var(--chat-input-surface-base) 88%,
        var(--streamlain-white, #ffffff) 12%
      );
      --chat-input-action-size: 28px;
      --chat-input-action-icon-size: 20px;
      --chat-input-effective-icon-inset: calc(
        (var(--chat-input-action-size) - var(--chat-input-action-icon-size)) / 2
      );
      display: flex;
      flex-direction: column;
      justify-content: flex-start;
      gap: 4px;
      position: relative;
      border-radius: 14px;
      padding: 8px 6px 6px 8px;
      min-height: 94px;
      max-height: min(46vh, 420px);
      box-sizing: border-box;
      overflow: visible;
      transition: box-shadow 0.23s ease;
      background-color: var(--chat-input-surface-background);

      &[data-independent-mode='true'] {
        padding: 12px;
        border-radius: 16px;
      }

      .chat-selection-quote {
        ${aiChatStatusTypography}
        padding: 4px 0px 8px 0px;
        padding-left: 15px;
        max-height: 56px;
        color: var(--streamlain-text-secondary-color);
        position: relative;

        div {
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }

        .chat-quote-close {
          position: absolute;
          right: 0;
          top: 0;
          cursor: pointer;
          display: none;
          width: 16px;
          height: 16px;
          border-radius: 4px;
          border: 1px solid var(--streamlain-border-color);
          background-color: var(--streamlain-white);
        }
      }

      .chat-selection-quote:hover .chat-quote-close {
        display: flex;
        justify-content: center;
        align-items: center;
      }

      .chat-selection-quote::after {
        content: '';
        width: 2px;
        height: calc(100% - 10px);
        margin-top: 5px;
        position: absolute;
        left: 0;
        top: 0;
        background: var(--streamlain-quote-color);
        border-radius: 20px;
      }
    }

    .chat-panel-input[data-initial-border='hold'] {
      --input-border-width: 2px;
      --input-border-color: var(--streamlain-primary-color);
      transition: box-shadow 80ms ease-out;
    }

    .chat-panel-input[data-initial-border='fade'] {
      transition: box-shadow 1500ms ease-out;
    }

    .chat-panel-input[data-initial-border='hold'] .chat-composer-tab {
      transition: border-color 80ms ease-out;
    }

    .chat-panel-input[data-initial-border='fade'] .chat-composer-tab {
      transition: border-color 1500ms ease-out;
    }

    @keyframes chat-goal-tab-lamp-pulse {
      0%,
      100% {
        box-shadow:
          0 0 0 1px var(--chat-goal-tab-lamp-ring),
          0 0 0 0 var(--chat-goal-tab-lamp-glow-soft);
      }

      50% {
        box-shadow:
          0 0 0 2px var(--chat-goal-tab-lamp-ring),
          0 0 8px 4px var(--chat-goal-tab-lamp-glow);
      }
    }

    .chat-panel-input-media-tray {
      flex: 0 0 auto;
      max-height: 172px;
      min-width: 0;
      overflow-y: auto;
      overflow-x: hidden;
    }

    .chat-panel-input-media-tray image-preview-grid {
      display: block;
      min-width: 0;
    }

    .chat-panel-context-tray {
      flex: 0 0 auto;
      width: 100%;
      min-width: 0;
      overflow: visible;
      box-sizing: border-box;
    }

    .chat-panel-context-tray chat-panel-chips {
      display: block;
      width: 100%;
      min-width: 0;
    }

    .chat-panel-context-tray .ai-chat-panel-chips {
      gap: 6px;
      align-content: flex-start;
      padding: 0 2px 2px 0;
      overflow: visible;
    }

    .chat-input-confirmation-card {
      display: block;
      flex: 0 0 auto;
      margin: 2px 2px 4px 0;
      min-width: 0;
      animation: chat-input-decision-reveal 180ms ease-out both;
    }

    @keyframes chat-input-decision-reveal {
      from {
        opacity: 0;
        transform: translateY(4px);
      }
      to {
        opacity: 1;
        transform: translateY(0);
      }
    }

    @media (prefers-reduced-motion: reduce) {
      .chat-input-confirmation-card {
        animation: none;
      }
    }

    .chat-input-confirmation-card chat-message-confirmation {
      display: block;
    }

    .chat-composer-tab-rail {
      position: absolute;
      right: 16px;
      bottom: 100%;
      left: 16px;
      z-index: 40;
      display: flex;
      align-items: flex-end;
      gap: 0;
      pointer-events: none;
    }

    .chat-composer-tab {
      ${aiChatControlTypography}
      --chat-composer-tab-background: color-mix(
        in srgb,
        var(--chat-input-surface-background) 94%,
        var(--chat-input-surface-base) 6%
      );
      position: relative;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      gap: 7px;
      height: 26px;
      padding: 0 11px;
      border: var(--input-border-width) solid var(--input-border-color);
      border-bottom: 0;
      border-radius: 10px 10px 0 0;
      background: var(--chat-composer-tab-background);
      color: var(--streamlain-v2-text-secondary);
      cursor: pointer;
      box-sizing: border-box;
      pointer-events: auto;
      box-shadow:
        inset 0 20px 24px rgba(0, 0, 0, 0.055),
        0 -1px 5px rgba(0, 0, 0, 0.08);
      transition:
        color 0.16s ease,
        background 0.16s ease,
        border-color 0.16s ease,
        box-shadow 0.16s ease;
    }

    .chat-composer-tab::after {
      content: '';
      position: absolute;
      right: 0;
      bottom: -1px;
      left: 0;
      height: 2px;
      background: var(--chat-composer-tab-background);
      pointer-events: none;
    }

    .chat-composer-tab:hover,
    .chat-composer-tab[data-active='true'] {
      color: var(--streamlain-v2-icon-activated);
    }

    .chat-composer-tab-spacer {
      flex: 1 1 auto;
    }

    .chat-composer-close {
      width: 26px;
      padding: 0;
      margin-left: 8px;
    }

    .chat-composer-close svg {
      width: 16px;
      height: 16px;
    }

    .chat-goal-tab {
      --chat-goal-tab-lamp-color: var(--streamlain-v2-icon-secondary);
      --chat-goal-tab-lamp-ring: transparent;
      --chat-goal-tab-lamp-glow: transparent;
      --chat-goal-tab-lamp-glow-soft: transparent;
      max-width: calc(100% - 32px);
    }

    .chat-goal-tab[data-tone='running'] {
      --chat-goal-tab-lamp-color: var(--streamlain-v2-icon-activated);
      --chat-goal-tab-lamp-ring: color-mix(
        in srgb,
        var(--streamlain-v2-icon-activated) 72%,
        transparent
      );
      --chat-goal-tab-lamp-glow: color-mix(
        in srgb,
        var(--streamlain-v2-icon-activated) 55%,
        transparent
      );
      --chat-goal-tab-lamp-glow-soft: color-mix(
        in srgb,
        var(--streamlain-v2-icon-activated) 24%,
        transparent
      );
    }

    .chat-goal-tab[data-tone='success'] {
      --chat-goal-tab-lamp-color: ${unsafeCSSVarV2('status/success')};
      --chat-goal-tab-lamp-ring: color-mix(
        in srgb,
        ${unsafeCSSVarV2('status/success')} 62%,
        transparent
      );
      --chat-goal-tab-lamp-glow: color-mix(
        in srgb,
        ${unsafeCSSVarV2('status/success')} 36%,
        transparent
      );
    }

    .chat-goal-tab[data-tone='waiting'] {
      --chat-goal-tab-lamp-color: var(--streamlain-v2-icon-activated);
      --chat-goal-tab-lamp-ring: color-mix(
        in srgb,
        var(--streamlain-v2-icon-activated) 68%,
        transparent
      );
      --chat-goal-tab-lamp-glow: color-mix(
        in srgb,
        var(--streamlain-v2-icon-activated) 46%,
        transparent
      );
    }

    .chat-goal-tab[data-tone='warning'] {
      --chat-goal-tab-lamp-color: ${unsafeCSSVarV2('status/warning' as any)};
      --chat-goal-tab-lamp-ring: color-mix(
        in srgb,
        ${unsafeCSSVarV2('status/warning' as any)} 62%,
        transparent
      );
      --chat-goal-tab-lamp-glow: color-mix(
        in srgb,
        ${unsafeCSSVarV2('status/warning' as any)} 42%,
        transparent
      );
    }

    .chat-goal-tab[data-tone='danger'] {
      --chat-goal-tab-lamp-color: ${unsafeCSSVarV2('status/error')};
      --chat-goal-tab-lamp-ring: color-mix(
        in srgb,
        ${unsafeCSSVarV2('status/error')} 68%,
        transparent
      );
      --chat-goal-tab-lamp-glow: color-mix(
        in srgb,
        ${unsafeCSSVarV2('status/error')} 48%,
        transparent
      );
    }

    .chat-goal-tab-dot {
      width: 7px;
      height: 7px;
      border-radius: 999px;
      background: var(--chat-goal-tab-lamp-color);
      opacity: 0.55;
      flex: 0 0 auto;
      transition:
        opacity 0.16s ease,
        background-color 0.16s ease,
        box-shadow 0.16s ease;
    }

    .chat-goal-tab[data-tone='running'] .chat-goal-tab-dot {
      opacity: 1;
      animation: chat-goal-tab-lamp-pulse 1.4s ease-in-out infinite;
    }

    .chat-goal-tab[data-tone='success'] .chat-goal-tab-dot,
    .chat-goal-tab[data-tone='waiting'] .chat-goal-tab-dot,
    .chat-goal-tab[data-tone='warning'] .chat-goal-tab-dot,
    .chat-goal-tab[data-tone='danger'] .chat-goal-tab-dot {
      opacity: 1;
      box-shadow:
        0 0 0 1px var(--chat-goal-tab-lamp-ring),
        0 0 7px 3px var(--chat-goal-tab-lamp-glow);
    }

    .chat-panel-input-actions {
      display: flex;
      flex: 0 0 auto;
      gap: 6px;
      align-items: center;
      width: 100%;
      min-width: 0;
      box-sizing: border-box;

      .chat-input-icon {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        min-width: 28px;
        max-width: 28px;
        height: 28px;
        flex: 0 0 28px;
        padding: 0;
        border: 0;
        border-radius: 8px;
        box-sizing: border-box;
        background: transparent;
        color: ${unsafeCSSVarV2('icon/primary')};
        cursor: pointer;
        transition:
          background-color 0.16s ease,
          color 0.16s ease,
          opacity 0.16s ease;

        svg {
          width: 20px;
          height: 20px;
          flex: 0 0 auto;
          color: ${unsafeCSSVarV2('icon/primary')};
        }

        .chat-input-icon-label {
          ${aiChatControlTypography}
          color: ${unsafeCSSVarV2('icon/primary')};
          margin: 0 4px 0 4px;
        }
      }

      chat-input-preference {
        display: inline-flex;
        flex: 0 1 auto;
        width: max-content;
        min-width: 0;
        max-width: 100%;
      }

      .chat-input-preference-trigger {
        width: 100%;
        max-width: 100%;
        min-width: 0;
      }

      .chat-input-preference-trigger-label {
        display: inline-flex;
        min-width: 0;
        max-width: 100%;
      }

      .chat-input-icon:nth-child(2) {
        margin-left: auto;
      }

      .chat-input-icon ai-chat-add-context,
      .chat-input-icon .ai-chat-add-context {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        width: 100%;
        height: 100%;
      }

      .chat-input-mode-button {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        min-width: 28px;
        max-width: 28px;
        height: 28px;
        flex: 0 0 28px;
        padding: 0;
        border: 0;
        border-radius: 8px;
        box-sizing: border-box;
        background: transparent;
        color: ${unsafeCSSVarV2('icon/primary')};
        cursor: pointer;
        transition:
          background-color 0.16s ease,
          color 0.16s ease,
          opacity 0.16s ease;
      }

      .chat-input-mode-button:hover {
        background-color: ${unsafeCSSVarV2('layer/background/hoverOverlay')};
      }

      .chat-input-mode-button[data-menu-open='true'] {
        background-color: ${unsafeCSSVarV2('layer/background/hoverOverlay')};
      }

      .chat-input-mode-button svg {
        width: 20px;
        height: 20px;
        flex: 0 0 auto;
      }

      .chat-input-mode-button[data-mode='auto'] {
        background: #1e96eb1f;
        color: ${unsafeCSSVarV2('icon/activated')};
      }

      .chat-input-mode-button[data-mode='confirm_each'] {
        background: #1e96eb1f;
        color: ${unsafeCSSVarV2('icon/activated')};
      }

      .chat-input-mode-button[data-mode='off'],
      .chat-input-mode-button[data-mode='chat'],
      .chat-input-mode-button[data-mode='autonomous'] {
        opacity: 0.64;
      }

      .chat-input-mode-button[data-mode='agent'] {
        background: #1e96eb1f;
        color: ${unsafeCSSVarV2('icon/activated')};
      }

      .chat-input-icon:hover {
        background-color: ${unsafeCSSVarV2('layer/background/hoverOverlay')};
      }

      .chat-input-icon[data-active='true'] {
        background-color: #1e96eb14;

        svg {
          color: ${unsafeCSSVarV2('icon/activated')};
        }

        .chat-input-icon-label {
          color: ${unsafeCSSVarV2('icon/activated')};
        }
      }

      .chat-input-icon[aria-disabled='true'] {
        cursor: not-allowed;

        svg {
          color: ${unsafeCSSVarV2('icon/secondary')};
        }
      }
    }

    .chat-panel-input {
      textarea {
        ${aiChatBodyTypography}
        flex: 1 1 auto;
        width: 100%;
        min-height: 22px;
        max-height: min(30vh, 260px);
        padding: 2px calc(var(--chat-input-effective-icon-inset) + 2px);
        margin: 0;
        border: none;
        color: var(--streamlain-text-primary-color);
        box-sizing: border-box;
        resize: none;
        overflow-y: hidden;
        background-color: transparent;
      }

      textarea::-webkit-scrollbar {
        -webkit-appearance: none;
        width: 4px;
        display: block;
      }

      textarea::-webkit-scrollbar:horizontal {
        height: 8px;
      }

      textarea::-webkit-scrollbar-thumb {
        border-radius: 2px;
        background-color: transparent;
      }

      textarea:hover::-webkit-scrollbar-thumb {
        border-radius: 16px;
        background-color: ${unsafeCSSVar('black30')};
      }

      textarea::placeholder {
        font: inherit;
        color: color-mix(
          in srgb,
          var(--streamlain-v2-text-placeholder) 72%,
          var(--streamlain-text-primary-color) 28%
        );
      }

      textarea:focus {
        outline: none;
      }
    }

    .chat-panel-input[data-if-focused='true'] {
      --input-border-width: 1px;
      --input-border-color: var(
        --streamlain-v2-layer-insideBorder-primaryBorder
      );
      user-select: none;
    }

    .chat-panel-send {
      display: flex;
      justify-content: center;
      align-items: center;
      width: 32px;
      height: 32px;
      flex-shrink: 0;
      border-radius: 50%;
      background: var(--streamlain-v2-icon-activated);
      color: var(--streamlain-v2-layer-pureWhite);
      border: none;
      padding: 0;
      cursor: pointer;
    }
    .chat-panel-send svg {
      width: ${aiChatIconSize.large};
      height: ${aiChatIconSize.large};
      display: block;
      flex-shrink: 0;
      stroke-width: ${STREAMLAIN_CHAT_ICON_STROKE_WIDTH};
    }
    .chat-panel-send[aria-disabled='true'] {
      cursor: not-allowed;
      background: var(--streamlain-v2-button-disable);
    }
    .chat-panel-send[data-offline='true'] {
      cursor: pointer;
      background: var(--streamlain-warning-color);
      color: var(--streamlain-v2-layer-pureWhite);
    }
    .chat-input-offline-status {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      gap: 0;
      position: absolute;
      top: 8px;
      right: 10px;
      z-index: 2;
      width: 24px;
      height: 24px;
      padding: 0;
      border: 0;
      border-radius: 999px;
      background: transparent;
      color: var(--streamlain-warning-color);
      white-space: nowrap;
      pointer-events: none;
      box-shadow: none;
    }
    .chat-input-offline-status svg {
      width: 16px;
      height: 16px;
      flex: 0 0 auto;
    }
    .chat-panel-stop {
      cursor: pointer;
      width: 28px;
      height: 28px;
      flex-shrink: 0;
      display: flex;
      justify-content: center;
      align-items: center;
      border-radius: 50%;
      color: var(--streamlain-v2-icon-activated);
      border: none;
      padding: 0;
      background: transparent;
    }
    .chat-panel-stop svg {
      width: ${aiChatIconSize.large};
      height: ${aiChatIconSize.large};
    }
    .chat-input-right-controls {
      display: inline-flex;
      align-items: center;
      gap: 6px;
      flex: 0 0 auto;
      margin-left: auto;
      min-width: 0;
    }

    /* E020-S007: STT Recording Styles */
    .chat-panel-mic {
      display: flex;
      justify-content: center;
      align-items: center;
      width: 32px;
      height: 32px;
      flex-shrink: 0;
      border-radius: 50%;
      border: none;
      padding: 0;
      cursor: pointer;
      background: transparent;
      color: ${unsafeCSSVarV2('icon/primary')};
      transition: all 0.2s ease;
    }
    .chat-panel-mic svg {
      width: ${aiChatIconSize.large};
      height: ${aiChatIconSize.large};
      display: block;
      flex-shrink: 0;
      stroke-width: ${STREAMLAIN_CHAT_ICON_STROKE_WIDTH};
    }
    .chat-panel-mic:hover {
      background: ${unsafeCSSVarV2('layer/background/hoverOverlay')};
    }
    .chat-panel-mic[aria-disabled='true'] {
      cursor: not-allowed;
      opacity: 0.5;
    }
    .chat-panel-mic.recording {
      color: #ef4444;
      animation: pulse-recording 1.5s ease-in-out infinite;
    }
    @keyframes pulse-recording {
      0%,
      100% {
        opacity: 1;
      }
      50% {
        opacity: 0.5;
      }
    }

    .recording-time {
      ${aiChatMetaTypography}
      flex: 0 0 auto;
      color: #ef4444;
      font-variant-numeric: tabular-nums;
      min-width: 36px;
      text-align: right;
    }
    .chat-panel-mic.transcribing {
      cursor: wait;
    }
    .chat-panel-mic.transcribing .spinner-icon {
      animation: spin 0.8s linear infinite;
      color: ${unsafeCSSVarV2('icon/activated')};
    }
    @keyframes spin {
      to {
        transform: rotate(360deg);
      }
    }

    @media (max-width: 600px), (pointer: coarse) {
      .chat-panel-input {
        --chat-input-action-size: 32px;
        --chat-input-action-icon-size: 19px;
        min-height: 98px;
        max-height: min(46vh, 320px);
        padding: 11px 8px;
        border-radius: 17px;
      }

      .chat-panel-input-media-tray {
        max-height: 136px;
      }

      .chat-panel-context-tray {
        overflow: visible;
      }

      .chat-panel-context-tray .ai-chat-panel-chips {
        padding-bottom: 2px;
      }

      .chat-panel-input-actions {
        gap: 4px;
      }

      .chat-panel-input-actions .chat-input-icon {
        min-width: 32px;
        max-width: 32px;
        min-height: 32px;
        height: 32px;
        flex-basis: 32px;
        padding: 0;
        border-radius: 8px;
      }

      .chat-panel-input-actions .chat-input-icon svg {
        width: 19px;
        height: 19px;
      }

      .chat-panel-input-actions chat-input-preference {
        max-width: 100%;
      }

      .chat-panel-input-actions .chat-input-preference-trigger {
        height: 32px;
        min-height: 32px;
        border-radius: 8px;
      }

      .chat-panel-input-actions .chat-input-preference-trigger-label {
        ${aiChatBadgeTypography}
        padding: 0 2px;
      }

      .chat-panel-input-actions .chat-input-mode-button {
        min-width: 32px;
        max-width: 32px;
        height: 32px;
        flex-basis: 32px;
        padding: 0;
        border-radius: 8px;
      }

      .chat-panel-input-actions .chat-input-mode-button svg {
        width: 19px;
        height: 19px;
      }

      .chat-input-right-controls {
        gap: 4px;
      }

      .chat-panel-send,
      .chat-panel-stop,
      .chat-panel-mic {
        width: 34px;
        height: 34px;
      }

      .chat-panel-send svg,
      .chat-panel-stop svg,
      .chat-panel-mic svg {
        width: ${aiChatIconSize.medium};
        height: ${aiChatIconSize.medium};
      }

      .chat-panel-mic {
        touch-action: none;
        background: ${unsafeCSSVarV2('layer/background/hoverOverlay')};
      }

      .chat-panel-mic.recording {
        background: rgba(239, 68, 68, 0.14);
      }
    }

    /* sl-w252: PDF thumbnail preview grid */
    .pdf-preview-grid {
      display: flex;
      gap: 8px;
      flex-wrap: nowrap;
      overflow-x: auto;
      overflow-y: hidden;
      padding: 4px 0 2px;
    }
    .pdf-preview-item {
      position: relative;
      display: flex;
      flex-direction: column;
      align-items: center;
      width: 64px;
    }
    .pdf-preview-item img {
      width: 58px;
      height: 72px;
      object-fit: cover;
      border-radius: 4px;
      border: 0.5px solid ${unsafeCSSVarV2('layer/insideBorder/border')};
    }
    .pdf-preview-item .pdf-name {
      ${aiChatBadgeTypography}
      color: ${unsafeCSSVarV2('text/secondary')};
      max-width: 64px;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
      text-align: center;
    }
    .pdf-preview-item .pdf-remove {
      position: absolute;
      top: -4px;
      right: -2px;
      width: 16px;
      height: 16px;
      border-radius: 50%;
      background: ${unsafeCSSVarV2('layer/background/primary')};
      border: 0.5px solid ${unsafeCSSVarV2('layer/insideBorder/border')};
      cursor: pointer;
      display: flex;
      align-items: center;
      justify-content: center;
    }
    .pdf-preview-item .pdf-remove svg {
      width: 12px;
      height: 12px;
      color: ${unsafeCSSVarV2('icon/primary')};
    }
  `;

  @property({ attribute: false })
  accessor independentMode: boolean | undefined;

  @property({ attribute: false })
  accessor canvasAIChatContextMode: CanvasAIChatContextMode = 'lineage-only';

  @property({ attribute: false })
  accessor autoFocusInput = false;

  @property({ attribute: false })
  accessor host: EditorHost | null | undefined;

  @property({ attribute: false })
  accessor workspaceId!: string;

  @property({ attribute: false })
  accessor docId: string | undefined;

  @property({ attribute: false })
  accessor session!: CopilotChatHistoryFragment | null | undefined;

  @property({ attribute: false })
  accessor goalTree: GoalTreeEnvelope | null = null;

  @property({ attribute: false })
  accessor goalStatus: string | null = null;

  @property({ attribute: false })
  accessor goalId: string | null = null;

  @property({ attribute: false })
  accessor goalBlocker: GoalPlanBlocker | null = null;

  @property({ attribute: false })
  accessor onGoalResume:
    | ((resumeData?: Record<string, unknown>) => void | Promise<void>)
    | null = null;

  @property({ attribute: false })
  accessor onGoalCancel: (() => void | Promise<void>) | null = null;

  @property({ attribute: false })
  accessor onGoalPause: (() => void | Promise<void>) | null = null;

  @query('textarea')
  accessor textarea!: HTMLTextAreaElement;

  private autoFocusFrame: number | null = null;
  private autoFocusTimer: number | null = null;
  private initialBorderFadeTimer: number | null = null;
  private initialBorderResetTimer: number | null = null;

  @state()
  private accessor initialBorderPhase: 'fade' | 'hold' | 'idle' = 'idle';

  @query('.chat-panel-input-actions')
  accessor inputActionsElement!: HTMLElement | null;

  @query('chat-input-preference')
  accessor modelPreferenceElement!: HTMLElement | null;

  @query('.chat-input-right-controls')
  accessor rightControlsElement!: HTMLElement | null;

  @state()
  accessor isInputEmpty = true;

  @state()
  accessor focused = false;

  @state()
  accessor modeMenuOpenFor:
    | 'web-search'
    | 'agent-mode'
    | 'confirmation-profile'
    | null = null;

  @state()
  accessor pendingTargetModeProposal: TargetModePermissionProposal | null =
    null;

  @state()
  accessor pendingToolConfirmation: ConfirmationMessage | null = null;

  @state()
  accessor completedConfirmationIds: string[] = [];

  // E020-S007: STT Recording State
  @state()
  accessor isRecording = false;

  @state()
  accessor isTranscribing = false;

  @state()
  accessor recordingSeconds = 0;

  @state()
  accessor modelSelectorAvailableWidth: number | null = null;

  /**
   * Index for navigating through message history with Arrow-Up/Down.
   * -1 means current input (not browsing history).
   */
  @state()
  accessor historyIndex = -1;

  /**
   * Stores the current input when user starts browsing history,
   * so it can be restored when they return to index -1.
   */
  private _savedCurrentInput = '';

  // E020-S007: Recording internals
  private mediaRecorder: MediaRecorder | null = null;
  private audioChunks: Blob[] = [];
  private recordingStartedAtMs: number | null = null;
  private skipNextTargetModeProposal = false;
  private targetModeProposalInFlight = false;
  private nextTargetRoutingDecision: 'accepted' | 'declined' | null = null;
  private readonly goalConfirmationRequestInFlight = new Set<string>();
  private recordingTimer: number | null = null;
  private modelSelectorResizeObserver: ResizeObserver | null = null;
  private modelSelectorObservedElements = new Set<Element>();
  private modelSelectorMeasureRaf = 0;

  @property({ attribute: false })
  accessor chatContextValue!: AIChatInputContext;

  @property({ attribute: false })
  accessor externalContextDocs: ExternalChatContextDoc[] = [];

  @property({ attribute: false })
  accessor chips: ChatChip[] = [];

  @property({ attribute: false })
  accessor isChipsCollapsed = false;

  @property({ attribute: false })
  accessor visibleDatabaseAttachments: DatabaseAttachment[] = [];

  @property({ attribute: false })
  accessor visibleAutomationAttachments: AutomationAttachment[] = [];

  @property({ attribute: false })
  accessor createSession!: () => Promise<
    CopilotChatHistoryFragment | undefined
  >;

  @property({ attribute: false })
  accessor updateContext!: (context: Partial<AIChatInputContext>) => void;

  @property({ attribute: false })
  accessor addImages!: (images: File[]) => void;

  // sl-w252: PDF paste handler (delegates to composer for page-count gating)
  @property({ attribute: false })
  accessor addPdf!: (file: File) => Promise<void>;

  // sl-w252: PDF remove handler (delegates to composer)
  @property({ attribute: false })
  accessor removePdf: ((pdf: PdfAttachment) => void) | undefined;

  @property({ attribute: false })
  accessor addChip!: (chip: ChatChip, silent?: boolean) => Promise<void>;

  @property({ attribute: false })
  accessor updateChip: (chip: ChatChip, options: Partial<ChatChip>) => void =
    () => {};

  @property({ attribute: false })
  accessor removeChip: (chip: ChatChip) => Promise<void> = async () => {};

  @property({ attribute: false })
  accessor toggleChipsCollapse: () => void = () => {};

  @property({ attribute: false })
  accessor removeDatabaseAttachment:
    | ((database: DatabaseAttachment) => void)
    | undefined;

  @property({ attribute: false })
  accessor removeAutomationAttachment:
    | ((automation: AutomationAttachment) => void)
    | undefined;

  @property({ attribute: false })
  accessor removeWorkflowAttachment:
    | ((workflow: WorkflowAttachment) => void)
    | undefined;

  @property({ attribute: false })
  accessor removeCodeSelection:
    | ((selection: CodeSelectionAttachment) => void)
    | undefined;

  @property({ attribute: false })
  accessor removeMailAttachment:
    | ((mail: MailMessageAttachment) => void)
    | undefined;

  @property({ attribute: false })
  accessor removePdfContextAttachment:
    | ((pdf: PdfContextAttachment) => void)
    | undefined;

  @property({ attribute: false })
  accessor removeCanvasSelectionItem:
    | ((chip: ChatChip, itemId: string) => void)
    | undefined;

  @property({ attribute: false })
  accessor removeCanvasSelectionItems:
    | ((chip: ChatChip, itemIds: string[]) => void)
    | undefined;

  @property({ attribute: false })
  accessor networkSearchConfig!: AINetworkSearchConfig;

  @property({ attribute: false })
  accessor reasoningConfig!: AIReasoningConfig;

  @property({ attribute: false })
  accessor docDisplayConfig!: DocDisplayConfig;

  @property({ attribute: false })
  accessor searchMenuConfig!: SearchMenuConfig;

  @property({ attribute: false })
  accessor serverService!: ServerService;

  @property({ attribute: false })
  accessor aiDraftService: AIDraftService | undefined;

  @property({ attribute: false })
  accessor aiToolsConfigService!: AIToolsConfigService;

  @property({ attribute: false })
  accessor affineFeatureFlagService!: FeatureFlagService;

  @property({ attribute: false })
  accessor notificationService!: NotificationService;

  @property({ attribute: false })
  accessor subscriptionService!: SubscriptionService;

  @property({ attribute: false })
  accessor aiModelService!: AIModelService;

  @property({ attribute: false })
  accessor onAISubscribe!: () => Promise<void>;

  @property({ attribute: false })
  accessor affineWorkspaceDialogService: WorkspaceDialogService | undefined;

  @property({ attribute: false })
  accessor isRootSession: boolean = true;

  @property({ attribute: false })
  accessor bots: BotOptionData[] = [];

  @property({ attribute: false })
  accessor onSelectBot: ((botId: string | null) => void) | undefined;

  @property({ attribute: false })
  accessor onChatSuccess: (() => void | Promise<void>) | undefined;

  @property({ attribute: false })
  accessor onChatStart: (() => void | Promise<void>) | undefined;

  @property({ attribute: false })
  accessor onChatError: ((error: unknown) => void | Promise<void>) | undefined;

  @property({ attribute: false })
  accessor excludedCanvasSelectionBlockIds: string[] = [];

  @property({ attribute: false })
  accessor trackOptions: BlockSuitePresets.TrackerOptions | undefined;

  @property({ attribute: 'data-testid', reflect: true })
  accessor testId = 'chat-panel-input-container';

  @property({ attribute: false })
  accessor portalContainer: HTMLElement | null = null;

  @state()
  accessor keySource: 'user' | 'workspace' | 'server' | 'none' = 'none';

  @state()
  accessor aiChatOnline = getAIChatOnlineStatus();

  private get _offlineAIMessage() {
    return I18n.t('com.streamlain.ai.offline.unavailable');
  }

  private get _sidebarOfflineBannerText() {
    return I18n.t('com.streamlain.streamlain.connectivity.offline-banner');
  }

  private get _offlineAIDetail() {
    return I18n.t('com.streamlain.ai.offline.detail');
  }

  private readonly _showOfflineAIToast = () => {
    this.notificationService?.notify({
      title: this._offlineAIMessage,
      message: this._offlineAIDetail,
      accent: 'warning',
    });
  };

  private get _isReasoningActive() {
    return isReasoningModeActive(
      this._selectedAIModel,
      this._selectedReasoningMode
    );
  }

  private get _selectedReasoningMode(): ReasoningModeId | undefined {
    return resolveReasoningMode(
      this._selectedAIModel,
      this.reasoningConfig.enabled.value,
      this.reasoningConfig.effort.value
    );
  }

  private get _selectedAIModel() {
    return resolveReasoningModel(
      this.aiModelService.models.value,
      this.aiModelService.modelId.value
    );
  }

  private get _retrievalMode() {
    return this.aiToolsConfigService.config.value.retrievalMode ?? 'auto';
  }

  private get _webSearchMode(): WebSearchToggleMode {
    return webSearchToggleModeForRetrievalMode(this._retrievalMode);
  }

  private get _agentMode(): AIAgentMode {
    return this.aiToolsConfigService.config.value.agentMode ?? 'chat';
  }

  private get _confirmationProfile(): AIConfirmationProfile {
    return (
      this.aiToolsConfigService.config.value.confirmationProfile ?? 'policy'
    );
  }

  @property({ attribute: false })
  accessor targetModeEnabled = false;

  @property({ attribute: false })
  accessor goalTabActive = false;

  @property({ attribute: false })
  accessor onRequestClose: (() => void) | undefined;

  private get _inputPlaceholder() {
    const agentMode = this._agentMode;
    if (agentMode === 'agent') {
      return I18n.t('com.streamlain.ai.chat.input.placeholder.agent');
    }
    return I18n.t('com.streamlain.ai.chat.input.placeholder.chat');
  }

  private _modeMenuHandler: StreamLainDropdownMenuHandler | null = null;

  private _setWebSearchMode(mode: WebSearchToggleMode) {
    this.aiToolsConfigService.setConfig({
      retrievalMode: setWebSearchToggleMode(this._retrievalMode, mode),
    });
    this._modeMenuHandler?.close();
  }

  private _setAgentMode(mode: AIAgentMode) {
    this.aiToolsConfigService.setConfig({
      agentMode: mode,
    });
    this._modeMenuHandler?.close();
  }

  private readonly _setConfirmationProfile = async (
    confirmationProfile: AIConfirmationProfile
  ) => {
    const previousProfile = this._confirmationProfile;
    const previousProposal = this.pendingTargetModeProposal;
    this.aiToolsConfigService.setConfig({ confirmationProfile });
    if (this.pendingTargetModeProposal) {
      this.pendingTargetModeProposal = {
        ...this.pendingTargetModeProposal,
        confirmationProfile,
      };
    }
    this._modeMenuHandler?.close();
    if (!this.goalId || !this._isGoalActivelyTracked()) return;
    try {
      const response = await workspaceApiFetch(
        `/api/workspace/${encodeURIComponent(this.workspaceId)}/agent-goals/${encodeURIComponent(this.goalId)}/confirmation-profile`,
        {
          method: 'PUT',
          credentials: 'include',
          headers: { 'content-type': 'application/json' },
          body: JSON.stringify({ confirmationProfile }),
        },
        {
          workspaceId: this.workspaceId,
          category: 'foreground-action',
          mode: 'foreground',
          priority: 'foreground',
        }
      );
      if (!response.ok) {
        throw new Error(
          `Target confirmation profile update failed with HTTP ${response.status}`
        );
      }
    } catch (error) {
      this.aiToolsConfigService.setConfig({
        confirmationProfile: previousProfile,
      });
      this.pendingTargetModeProposal = previousProposal;
      console.error('Failed to update target confirmation profile', error);
    }
  };

  override connectedCallback() {
    super.connectedCallback();

    const updateAIChatOnline = () => {
      this.aiChatOnline = getAIChatOnlineStatus();
    };

    updateAIChatOnline();
    this._disposables.add(subscribeAIChatOnlineStatus(updateAIChatOnline));
    this._disposables.add(
      confirmationEventBus.subscribe(this._handleConfirmationRequired)
    );

    void this._fetchKeySource();
    void this._syncModelPreference();

    // Re-sync when settings change the model (CustomEvent from settings hooks)
    const onModelChanged = () => {
      void this._syncModelPreferenceFromSettings();
    };
    window.addEventListener('sl:ai-model-changed', onModelChanged);
    this._disposables.add(() => {
      window.removeEventListener('sl:ai-model-changed', onModelChanged);
    });

    this._disposables.add(
      AIProvider.slots.requestSendWithChat.subscribe(
        (params: AISendParams | null) => {
          if (!params) {
            return;
          }
          const { input, context, host } = params;
          if (this.host === host) {
            if (context) {
              this.updateContext(context);
            }
            setTimeout(() => {
              this.send(input).catch(console.error);
            }, 0);
          }
          AIProvider.slots.requestSendWithChat.next(null);
        }
      )
    );

    this._disposables.add(
      AIProvider.slots.requestOpenWithChat.subscribe(params => {
        if (!params) return;

        const { input, host, workspaceId } = params;
        const targetsThisChat = host
          ? this.host === host
          : Boolean(workspaceId && workspaceId === this.workspaceId);
        if (!targetsThisChat) return;

        if (input) {
          this.textarea.value = input;
          this.isInputEmpty = !this.textarea.value.trim();
          this._syncTextareaHeight();
        }
      })
    );
  }

  override disconnectedCallback() {
    this._clearInitialInputActivation();
    this._clearRecordingTimer();
    this._disconnectModelSelectorResizeObserver();
    if (this.modelSelectorMeasureRaf) {
      cancelAnimationFrame(this.modelSelectorMeasureRaf);
      this.modelSelectorMeasureRaf = 0;
    }
    super.disconnectedCallback();
  }

  protected override firstUpdated(changedProperties: PropertyValues): void {
    super.firstUpdated(changedProperties);
    this._syncModelSelectorResizeObserver();
    this._scheduleModelSelectorWidthUpdate();
    // Every host uses the same initial textarea sizing contract. Draft
    // hydration may replace the value afterwards, but must not be what enables
    // autosizing only for the sidebar composer.
    this._syncTextareaHeight();
    if (this.autoFocusInput) this._activateInitialInput();
    if (this.aiDraftService) {
      this.aiDraftService
        .getDraft()
        .then(draft => {
          this.textarea.value = draft.input;
          this.isInputEmpty = !this.textarea.value.trim();
          this._syncTextareaHeight();
        })
        .catch(console.error);
    }
  }

  private _activateInitialInput() {
    this._clearInitialInputActivation();
    this.initialBorderPhase = 'hold';
    this.initialBorderFadeTimer = window.setTimeout(() => {
      this.initialBorderFadeTimer = null;
      this.initialBorderPhase = 'fade';
      this.initialBorderResetTimer = window.setTimeout(() => {
        this.initialBorderResetTimer = null;
        this.initialBorderPhase = 'idle';
      }, 1500);
    }, 800);

    const focus = () => {
      if (!this.isConnected || this._hasPendingDecision()) return;
      this.textarea.focus({ preventScroll: true });
    };
    focus();
    this.autoFocusFrame = requestAnimationFrame(() => {
      this.autoFocusFrame = null;
      focus();
      this.autoFocusTimer = window.setTimeout(() => {
        this.autoFocusTimer = null;
        focus();
      }, 260);
    });
  }

  private _clearInitialInputActivation() {
    if (this.autoFocusFrame !== null) {
      cancelAnimationFrame(this.autoFocusFrame);
      this.autoFocusFrame = null;
    }
    if (this.autoFocusTimer !== null) {
      window.clearTimeout(this.autoFocusTimer);
      this.autoFocusTimer = null;
    }
    if (this.initialBorderFadeTimer !== null) {
      window.clearTimeout(this.initialBorderFadeTimer);
      this.initialBorderFadeTimer = null;
    }
    if (this.initialBorderResetTimer !== null) {
      window.clearTimeout(this.initialBorderResetTimer);
      this.initialBorderResetTimer = null;
    }
    this.initialBorderPhase = 'idle';
  }

  protected override updated(changedProperties: PropertyValues): void {
    super.updated(changedProperties);
    this._syncModelSelectorResizeObserver();
    this._scheduleModelSelectorWidthUpdate();
    if (
      changedProperties.has('goalStatus') ||
      changedProperties.has('goalId') ||
      changedProperties.has('goalBlocker') ||
      changedProperties.has('session')
    ) {
      void this._ensureGoalConfirmationRequestPersisted();
    }
  }

  private _disconnectModelSelectorResizeObserver() {
    this.modelSelectorResizeObserver?.disconnect();
    this.modelSelectorResizeObserver = null;
    this.modelSelectorObservedElements.clear();
  }

  private _syncModelSelectorResizeObserver() {
    if (typeof ResizeObserver === 'undefined') {
      this._scheduleModelSelectorWidthUpdate();
      return;
    }

    const observedElements = [
      this.inputActionsElement,
      this.modelPreferenceElement,
      this.rightControlsElement,
    ].filter((element): element is HTMLElement => !!element);
    const nextObservedElements = new Set<Element>(observedElements);

    this.modelSelectorResizeObserver ??= new ResizeObserver(() => {
      this._scheduleModelSelectorWidthUpdate();
    });

    for (const element of this.modelSelectorObservedElements) {
      if (!nextObservedElements.has(element)) {
        this.modelSelectorResizeObserver.unobserve(element);
      }
    }

    for (const element of nextObservedElements) {
      if (!this.modelSelectorObservedElements.has(element)) {
        this.modelSelectorResizeObserver.observe(element);
      }
    }

    this.modelSelectorObservedElements = nextObservedElements;
  }

  private _scheduleModelSelectorWidthUpdate() {
    if (this.modelSelectorMeasureRaf) return;
    this.modelSelectorMeasureRaf = requestAnimationFrame(() => {
      this.modelSelectorMeasureRaf = 0;
      this._updateModelSelectorWidth();
    });
  }

  private _updateModelSelectorWidth() {
    const inputActions = this.inputActionsElement;
    const modelPreference = this.modelPreferenceElement;
    const rightControls = this.rightControlsElement;

    if (!inputActions || !modelPreference || !rightControls) {
      if (this.modelSelectorAvailableWidth !== null) {
        this.modelSelectorAvailableWidth = null;
      }
      return;
    }

    const preferenceRect = modelPreference.getBoundingClientRect();
    const rightControlsRect = rightControls.getBoundingClientRect();
    const actionsStyle = getComputedStyle(inputActions);
    const gap =
      Number.parseFloat(actionsStyle.columnGap || actionsStyle.gap || '0') || 0;
    const availableWidth = Math.floor(
      rightControlsRect.left - preferenceRect.left - gap
    );
    const nextWidth = Math.max(0, availableWidth);

    if (
      this.modelSelectorAvailableWidth === null ||
      Math.abs(this.modelSelectorAvailableWidth - nextWidth) > 1
    ) {
      this.modelSelectorAvailableWidth = nextWidth;
    }
  }

  private get _hasVisibleContext() {
    const pdfContextAttachments =
      this.chatContextValue.pdfContextAttachments || [];
    const coveredContextBlockIds = [
      ...this.visibleDatabaseAttachments.map(attachment => attachment.blockId),
      ...this.visibleAutomationAttachments.map(
        attachment => attachment.blockId
      ),
      ...pdfContextAttachments.map(attachment => attachment.blockId),
    ];
    const selectedContextCount = this.chips
      .filter(isSelectedContextChip)
      .map(chip =>
        filterCanvasSelectionsByCoveredBlockIds(
          chip.canvasSelections ?? [],
          coveredContextBlockIds,
          this.canvasAIChatContextMode
        )
      )
      .filter(canvasSelections => canvasSelections.length > 0).length;

    return (
      this.chips.some(chip => isDocChip(chip) && chip.state !== 'candidate') ||
      this.chips.some(isFileChip) ||
      this.chips.some(isAttachmentChip) ||
      this.chips.some(isTagChip) ||
      this.chips.some(isCollectionChip) ||
      this.visibleDatabaseAttachments.length > 0 ||
      this.visibleAutomationAttachments.length > 0 ||
      (this.chatContextValue.workflowAttachments || []).length > 0 ||
      (this.chatContextValue.codeSelections || []).length > 0 ||
      (this.chatContextValue.mailAttachments || []).length > 0 ||
      pdfContextAttachments.length > 0 ||
      this.externalContextDocs.length > 0 ||
      selectedContextCount > 0
    );
  }

  private _renderMediaTray(images: File[], pdfAttachments: PdfAttachment[]) {
    if (images.length === 0 && pdfAttachments.length === 0) return nothing;

    return html`<div
      class="chat-panel-input-media-tray"
      data-chat-input-interactive
    >
      ${images.length > 0
        ? html`
            <image-preview-grid
              .images=${images}
              .onImageRemove=${this._handleImageRemove}
            ></image-preview-grid>
          `
        : nothing}
      ${pdfAttachments.length > 0
        ? html`
            <div class="pdf-preview-grid">
              ${repeat(
                pdfAttachments,
                pdf => pdf.name,
                pdf => html`
                  <div class="pdf-preview-item">
                    ${pdf.thumbnailDataUrl
                      ? html`<img
                          src=${pdf.thumbnailDataUrl}
                          alt=${pdf.name}
                        />`
                      : nothing}
                    <span class="pdf-name"
                      >${pdf.name} (${pdf.pageCount}p)</span
                    >
                    <div
                      class="pdf-remove"
                      @click=${() => this.removePdf?.(pdf)}
                    >
                      ${CloseIcon()}
                    </div>
                  </div>
                `
              )}
            </div>
          `
        : nothing}
    </div>`;
  }

  private _renderContextTray() {
    if (!this._hasVisibleContext) return nothing;

    return html`<div
      class="chat-panel-context-tray"
      data-chat-input-interactive
    >
      <chat-panel-chips
        .chips=${this.chips}
        .isCollapsed=${this.isChipsCollapsed}
        .independentMode=${this.independentMode}
        .addChip=${this.addChip}
        .updateChip=${this.updateChip}
        .removeChip=${this.removeChip}
        .toggleCollapse=${this.toggleChipsCollapse}
        .docDisplayConfig=${this.docDisplayConfig}
        .portalContainer=${this.portalContainer}
        .contextOnly=${true}
        .canvasAIChatContextMode=${this.canvasAIChatContextMode}
        .databaseAttachments=${this.visibleDatabaseAttachments}
        .removeDatabaseAttachment=${this.removeDatabaseAttachment}
        .automationAttachments=${this.visibleAutomationAttachments}
        .removeAutomationAttachment=${this.removeAutomationAttachment}
        .workflowAttachments=${this.chatContextValue.workflowAttachments || []}
        .removeWorkflowAttachment=${this.removeWorkflowAttachment}
        .codeSelections=${this.chatContextValue.codeSelections || []}
        .removeCodeSelection=${this.removeCodeSelection}
        .mailAttachments=${this.chatContextValue.mailAttachments || []}
        .removeMailAttachment=${this.removeMailAttachment}
        .pdfContextAttachments=${this.chatContextValue.pdfContextAttachments ||
        []}
        .genericContextItems=${buildCanonicalMessageContext({
          docContexts: this.externalContextDocs,
        }).items}
        .removePdfContextAttachment=${this.removePdfContextAttachment}
        .removeCanvasSelectionItem=${this.removeCanvasSelectionItem}
        .removeCanvasSelectionItems=${this.removeCanvasSelectionItems}
      ></chat-panel-chips>
    </div>`;
  }

  private _renderModeButton({
    mode,
    title,
    ariaLabel,
    icon,
    onClick,
    testId,
    menuOpen,
  }: {
    mode: WebSearchToggleMode | AIAgentMode | AIConfirmationProfile;
    title: string;
    ariaLabel: string;
    icon: unknown;
    onClick: (event: MouseEvent) => void;
    testId: string;
    menuOpen: boolean;
  }) {
    return html`<button
      class="chat-input-mode-button"
      data-mode=${mode}
      data-menu-open=${menuOpen}
      data-testid=${testId}
      title=${title}
      aria-label=${ariaLabel}
      aria-haspopup="menu"
      aria-expanded=${menuOpen ? 'true' : 'false'}
      @click=${onClick}
    >
      ${icon}
    </button>`;
  }

  private readonly _openWebSearchMenu = (event: MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();
    const element = event.currentTarget;
    if (!(element instanceof HTMLElement)) return;

    const currentMode = this._webSearchMode;
    const autoLabel = I18n.t('com.streamlain.ai.chat.input.web-search.auto');
    const offLabel = I18n.t('com.streamlain.ai.chat.input.web-search.off');
    const boundary = getChatInputPortalReferenceElement(this, element);
    const menuWidth = `min(${MODE_MENU_WIDTH}, ${streamLainDropdownMenuBoundaryWidthCss(
      MODE_MENU_WIDTH
    )})`;
    this._modeMenuHandler?.close();
    this.modeMenuOpenFor = 'web-search';
    this._modeMenuHandler = popMenu(popupTargetFromBodyElement(element), {
      options: {
        testId: 'chat-web-search-menu',
        items: [
          menu.group({
            items: [
              menu.action({
                name: autoLabel,
                label: modeMenuLabel(autoLabel),
                isSelected: currentMode === 'auto',
                select: () => this._setWebSearchMode('auto'),
              }),
              menu.action({
                name: offLabel,
                label: modeMenuLabel(offLabel),
                isSelected: currentMode === 'off',
                select: () => this._setWebSearchMode('off'),
              }),
            ],
          }),
        ],
      },
      placement: 'top',
      middleware: streamLainDropdownMenuMiddleware({
        horizontalBoundary: boundary,
      }),
    });
    const currentHandler = this._modeMenuHandler;
    configureStreamLainDropdownMenu(currentHandler, element, {
      layout: {
        width: menuWidth,
        minWidth: menuWidth,
        maxWidth: streamLainDropdownMenuBoundaryWidthCss('calc(100vw - 16px)'),
      },
      onClosed: () => {
        if (this._modeMenuHandler === currentHandler) {
          this._modeMenuHandler = null;
          this.modeMenuOpenFor = null;
        }
      },
    });
  };

  private readonly _openAgentModeMenu = (event: MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();
    const element = event.currentTarget;
    if (!(element instanceof HTMLElement)) return;

    const currentMode = this._agentMode;
    const chatLabel = I18n.t('com.streamlain.ai.chat.input.agent-mode.chat');
    const agentLabel = I18n.t('com.streamlain.ai.chat.input.agent-mode.agent');
    const chatDescription = I18n.t(
      'com.streamlain.ai.chat.input.agent-mode.chat-description'
    );
    const agentDescription = I18n.t(
      'com.streamlain.ai.chat.input.agent-mode.agent-description'
    );
    const boundary = getChatInputPortalReferenceElement(this, element);
    const menuWidth = `min(${MODE_MENU_WIDTH}, ${streamLainDropdownMenuBoundaryWidthCss(
      MODE_MENU_WIDTH
    )})`;
    this._modeMenuHandler?.close();
    this.modeMenuOpenFor = 'agent-mode';
    this._modeMenuHandler = popMenu(popupTargetFromBodyElement(element), {
      options: {
        testId: 'chat-agent-mode-menu',
        items: [
          menu.group({
            items: [
              menu.action({
                name: agentLabel,
                label: modeMenuLabel(agentLabel, agentDescription),
                isSelected: currentMode === 'agent',
                select: () => this._setAgentMode('agent'),
              }),
              menu.action({
                name: chatLabel,
                label: modeMenuLabel(chatLabel, chatDescription),
                isSelected: currentMode === 'chat',
                select: () => this._setAgentMode('chat'),
              }),
            ],
          }),
        ],
      },
      placement: 'top',
      middleware: streamLainDropdownMenuMiddleware({
        horizontalBoundary: boundary,
      }),
    });
    const currentHandler = this._modeMenuHandler;
    configureStreamLainDropdownMenu(currentHandler, element, {
      layout: {
        width: menuWidth,
        minWidth: menuWidth,
        maxWidth: streamLainDropdownMenuBoundaryWidthCss('calc(100vw - 16px)'),
      },
      onClosed: () => {
        if (this._modeMenuHandler === currentHandler) {
          this._modeMenuHandler = null;
          this.modeMenuOpenFor = null;
        }
      },
    });
  };

  private readonly _openConfirmationProfileMenu = (event: MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();
    const element = event.currentTarget;
    if (!(element instanceof HTMLElement)) return;
    const currentProfile = this._confirmationProfile;
    const profiles: readonly AIConfirmationProfile[] = [
      'confirm_each',
      'policy',
      'autonomous',
    ];
    const boundary = getChatInputPortalReferenceElement(this, element);
    const menuWidth = `min(${MODE_MENU_WIDTH}, ${streamLainDropdownMenuBoundaryWidthCss(
      MODE_MENU_WIDTH
    )})`;
    this._modeMenuHandler?.close();
    this.modeMenuOpenFor = 'confirmation-profile';
    this._modeMenuHandler = popMenu(popupTargetFromBodyElement(element), {
      options: {
        testId: 'chat-confirmation-profile-menu',
        items: [
          menu.group({
            items: profiles.map(profile => {
              const label = I18n.t(
                `com.streamlain.ai.confirmation.profile.${profile === 'confirm_each' ? 'confirm-each' : profile}`
              );
              return menu.action({
                name: label,
                label: modeMenuLabel(label),
                isSelected: currentProfile === profile,
                select: () => void this._setConfirmationProfile(profile),
              });
            }),
          }),
        ],
      },
      placement: 'top',
      middleware: streamLainDropdownMenuMiddleware({
        horizontalBoundary: boundary,
      }),
    });
    const currentHandler = this._modeMenuHandler;
    configureStreamLainDropdownMenu(currentHandler, element, {
      layout: {
        width: menuWidth,
        minWidth: menuWidth,
        maxWidth: streamLainDropdownMenuBoundaryWidthCss('calc(100vw - 16px)'),
      },
      onClosed: () => {
        if (this._modeMenuHandler === currentHandler) {
          this._modeMenuHandler = null;
          this.modeMenuOpenFor = null;
        }
      },
    });
  };

  private _renderConfirmationProfileButton() {
    const profile = this._confirmationProfile;
    const label = I18n.t(
      `com.streamlain.ai.confirmation.profile.${profile === 'confirm_each' ? 'confirm-each' : profile}`
    );
    return this._renderModeButton({
      mode: profile,
      title: `${I18n.t('com.streamlain.ai.confirmation.profile.label')}: ${label}`,
      ariaLabel: `${I18n.t('com.streamlain.ai.confirmation.profile.label')}: ${label}`,
      icon: StopHandIcon(),
      onClick: this._openConfirmationProfileMenu,
      testId: 'chat-confirmation-profile',
      menuOpen: this.modeMenuOpenFor === 'confirmation-profile',
    });
  }

  private _renderWebSearchButton() {
    const mode = this._webSearchMode;
    const label = webSearchModeLabel(mode);
    return this._renderModeButton({
      mode,
      title: `Websuche: ${label}`,
      ariaLabel: `Websuche: ${label}`,
      icon: WebIcon(),
      onClick: this._openWebSearchMenu,
      testId: 'chat-web-search-mode',
      menuOpen: this.modeMenuOpenFor === 'web-search',
    });
  }

  private _renderAgentModeButton() {
    const mode = this._agentMode;
    const label = agentModeLabel(mode);
    return this._renderModeButton({
      mode,
      title: `Modus: ${label}`,
      ariaLabel: `Modus: ${label}`,
      icon: ThinkingIcon(),
      onClick: this._openAgentModeMenu,
      testId: 'chat-agent-mode',
      menuOpen: this.modeMenuOpenFor === 'agent-mode',
    });
  }

  private _isGoalActivelyTracked() {
    if (!this.goalTree) return false;
    const status = (this.goalStatus ?? '').toLowerCase();
    if (
      status === 'queued' ||
      status === 'running' ||
      status === 'paused' ||
      status === 'waiting_for_confirmation' ||
      status === 'waiting_for_user'
    ) {
      return true;
    }
    if (
      status === 'completed' ||
      status === 'failed' ||
      status === 'cancelled'
    ) {
      return false;
    }
    const { progress } = this.goalTree;
    return (progress?.completed ?? 0) < (progress?.total ?? 0);
  }

  private _goalTabTone():
    | 'idle'
    | 'running'
    | 'waiting'
    | 'warning'
    | 'danger'
    | 'success' {
    if (!this.goalTree) return 'idle';
    const status = (this.goalStatus ?? '').toLowerCase();
    const { progress } = this.goalTree;
    if (status === 'failed') return 'danger';
    if (status === 'cancelled') return 'idle';
    if (status === 'completed') return 'success';
    if (status === 'paused') return 'waiting';
    if ((progress?.blocked ?? 0) > 0) return 'danger';
    if ((progress?.invalid ?? 0) > 0 || (progress?.missing ?? 0) > 0) {
      return 'warning';
    }
    if (
      status === 'waiting_for_confirmation' ||
      status === 'waiting_for_user'
    ) {
      return 'waiting';
    }
    if (status === 'queued' || status === 'running') return 'running';
    return this._isGoalActivelyTracked() ? 'running' : 'success';
  }

  private _renderGoalTab() {
    const active = this._isGoalActivelyTracked();
    const tone = this._goalTabTone();
    const targetLabel = I18n.t('com.streamlain.ai.target.label');
    return html`<button
      class="chat-composer-tab chat-goal-tab"
      data-testid="chat-goal-tree-toggle"
      data-active=${this.goalTabActive ? 'true' : 'false'}
      data-running=${active ? 'true' : 'false'}
      data-tone=${tone}
      title=${targetLabel}
      aria-label=${targetLabel}
      @click=${(event: MouseEvent) => {
        event.preventDefault();
        event.stopPropagation();
        window.dispatchEvent(
          new CustomEvent('streamlain:goal-tree-toggle', {
            detail: { workspaceId: this.workspaceId },
          })
        );
      }}
    >
      <span class="chat-goal-tab-dot"></span>
      <span>${targetLabel}</span>
    </button>`;
  }

  private _renderComposerTabRail() {
    if (!this.targetModeEnabled && !this.onRequestClose) return nothing;
    const closeLabel = I18n.t('com.streamlain.peek-view-controls.close');

    return html`<div
      class="chat-composer-tab-rail"
      data-testid="chat-composer-tab-rail"
    >
      <span class="chat-composer-tab-spacer"></span>
      ${this.targetModeEnabled ? this._renderGoalTab() : nothing}
      ${this.onRequestClose
        ? html`<button
            class="chat-composer-tab chat-composer-close"
            data-testid="chat-composer-close"
            title=${closeLabel}
            aria-label=${closeLabel}
            @click=${(event: MouseEvent) => {
              event.preventDefault();
              event.stopPropagation();
              this.onRequestClose?.();
            }}
          >
            ${CloseIcon()}
          </button>`
        : nothing}
    </div>`;
  }

  private _renderTargetModeProposal() {
    const proposal = this.pendingTargetModeProposal;
    if (!proposal) return nothing;
    const confirmationItem: ConfirmationMessage = {
      type: 'confirmation',
      id: proposal.approvalId,
      toolName: proposal.includesAgentMode ? 'agent_mode' : 'target_mode',
      displayName: I18n.t(
        'com.streamlain.ai.chat.input.target.proposal.display-name'
      ),
      title: I18n.t(
        proposal.includesAgentMode
          ? proposal.includesTargetMode
            ? 'com.streamlain.ai.chat.input.target.proposal.title.combined'
            : 'com.streamlain.ai.chat.input.target.proposal.title.agent'
          : 'com.streamlain.ai.chat.input.target.proposal.title.target'
      ),
      description: proposal.description,
      copy: proposal.copy,
      confirmLabel: I18n.t(
        proposal.includesAgentMode
          ? proposal.includesTargetMode
            ? 'com.streamlain.ai.chat.input.target.proposal.confirm.combined'
            : 'com.streamlain.ai.chat.input.target.proposal.confirm.agent'
          : 'com.streamlain.ai.chat.input.target.proposal.confirm.target'
      ),
      cancelLabel: I18n.t(
        proposal.includesAgentMode
          ? 'com.streamlain.ai.chat.input.target.proposal.cancel.chat'
          : 'com.streamlain.ai.chat.input.target.proposal.cancel.target'
      ),
      settingsHint: null,
      metadata: {
        domain: proposal.domain,
        includesAgentMode: proposal.includesAgentMode,
        includesTargetMode: proposal.includesTargetMode,
      },
    };
    return html`<div
      class="chat-input-confirmation-card target-mode-proposal"
      data-testid="chat-target-mode-proposal"
      @pointerdown=${(event: PointerEvent) => event.stopPropagation()}
    >
      <chat-message-confirmation
        .item=${confirmationItem}
        .confirmationProfile=${proposal.confirmationProfile}
        .showConfirmationProfile=${true}
        .onConfirmationProfileChange=${(profile: AIConfirmationProfile) =>
          void this._setConfirmationProfile(profile)}
        .onRespond=${this._handleTargetModeProposalResponse}
      ></chat-message-confirmation>
    </div>`;
  }

  private _resolvePendingToolConfirmation(): ConfirmationMessage | null {
    if (
      this.pendingToolConfirmation &&
      !this.completedConfirmationIds.includes(this.pendingToolConfirmation.id)
    ) {
      return this.pendingToolConfirmation;
    }
    return findLatestPersistedPendingConfirmation(
      this.chatContextValue.messages,
      this.completedConfirmationIds
    );
  }

  private _renderToolConfirmation() {
    if (this.pendingTargetModeProposal || this._hasGoalConfirmation()) {
      return nothing;
    }
    const confirmation = this._resolvePendingToolConfirmation();
    if (!confirmation) return nothing;
    if (confirmation.toolName === 'agent_goal') return nothing;
    return html`<div
      class="chat-input-confirmation-card"
      data-testid="chat-tool-confirmation"
      @pointerdown=${(event: PointerEvent) => event.stopPropagation()}
    >
      <chat-message-confirmation
        .item=${confirmation}
        .sessionId=${this.session?.sessionId}
        .workspaceId=${this.workspaceId}
        .onRespond=${confirmation.toolName === 'target_mode' ||
        confirmation.toolName === 'agent_mode'
          ? (approved: boolean) =>
              this._handlePersistedTargetModeProposalResponse(
                confirmation,
                approved
              )
          : undefined}
        @confirmation-responded=${() =>
          this._handleToolConfirmationResponded(confirmation.id)}
      ></chat-message-confirmation>
    </div>`;
  }

  private _hasGoalConfirmation(): boolean {
    return (
      this.goalStatus === 'waiting_for_confirmation' &&
      this.goalBlocker?.kind === 'confirmation' &&
      !!this.goalId
    );
  }

  private _hasPendingDecision(): boolean {
    return (
      !!this.pendingTargetModeProposal ||
      this._hasGoalConfirmation() ||
      !!this._resolvePendingToolConfirmation()
    );
  }

  private _goalConfirmationDescription(): string {
    if (this.goalBlocker?.code === 'custom_app_goal_confirmation_required') {
      return I18n.t('com.streamlain.ai.target.confirmation.create-app');
    }
    return I18n.t('com.streamlain.ai.target.confirmation.continue');
  }

  private _goalConfirmationCopy() {
    return {
      key:
        this.goalBlocker?.code === 'custom_app_goal_confirmation_required'
          ? 'com.streamlain.ai.target.confirmation.create-app'
          : 'com.streamlain.ai.target.confirmation.continue',
    } as const;
  }

  private _goalConfirmationPermission(): GoalConfirmationPermission | null {
    const sessionId = this.session?.sessionId;
    const blockerCode = this.goalBlocker?.code;
    if (
      !this._hasGoalConfirmation() ||
      !sessionId ||
      !this.goalId ||
      !blockerCode
    ) {
      return null;
    }
    return {
      goalId: this.goalId,
      blockerCode,
      approvalId: `agent-goal:${this.goalId}:${blockerCode}`,
      sessionId,
      description: this._goalConfirmationDescription(),
      copy: this._goalConfirmationCopy(),
    };
  }

  private _hasPermissionHistoryMessage(
    approvalId: string,
    messageType: 'tool_approval_request' | 'tool_approval_response'
  ): boolean {
    return this.chatContextValue.messages.some(
      item =>
        isChatMessage(item) &&
        readMessageType(item) === messageType &&
        readToolData(item)?.approvalId === approvalId
    );
  }

  private async _persistGoalConfirmationRequest(
    permission: GoalConfirmationPermission
  ): Promise<void> {
    if (
      this._hasPermissionHistoryMessage(
        permission.approvalId,
        'tool_approval_request'
      )
    ) {
      return;
    }
    const message = await persistGoalConfirmationRequest(
      permission,
      this.workspaceId,
      this.docId,
      this.serverService.server.fetch
    );
    this._appendPermissionHistoryMessage(message);
  }

  private async _ensureGoalConfirmationRequestPersisted(): Promise<void> {
    const permission = this._goalConfirmationPermission();
    if (
      !permission ||
      this.goalConfirmationRequestInFlight.has(permission.approvalId)
    ) {
      return;
    }
    this.goalConfirmationRequestInFlight.add(permission.approvalId);
    try {
      await this._persistGoalConfirmationRequest(permission);
    } catch (error) {
      console.error('Failed to persist goal confirmation request', error);
    } finally {
      this.goalConfirmationRequestInFlight.delete(permission.approvalId);
    }
  }

  private readonly _handleGoalConfirmationResponse = async (
    approved: boolean
  ): Promise<ConfirmationMessage['toolResult']> => {
    const permission = this._goalConfirmationPermission();
    if (!permission) {
      return { success: false, error: 'Permission context unavailable' };
    }
    await this._persistGoalConfirmationRequest(permission);
    if (
      !this._hasPermissionHistoryMessage(
        permission.approvalId,
        'tool_approval_response'
      )
    ) {
      const message = await persistGoalConfirmationResponse(
        permission,
        approved,
        this.workspaceId,
        this.docId,
        this.serverService.server.fetch
      );
      this._appendPermissionHistoryMessage(message);
    }
    if (approved) {
      const acceptedConfirmations =
        this.goalBlocker?.options.map(option => option.id) ?? [];
      await this.onGoalResume?.({ acceptedConfirmations });
      return { success: true };
    }
    await this.onGoalPause?.();
    return { success: true };
  };

  private _renderGoalConfirmation() {
    if (this.pendingTargetModeProposal || !this._hasGoalConfirmation()) {
      return nothing;
    }
    const permission = this._goalConfirmationPermission();
    if (!permission) return nothing;
    const item: ConfirmationMessage = {
      type: 'confirmation',
      id: permission.approvalId,
      toolName: 'agent_goal_resume',
      displayName: I18n.t('com.streamlain.ai.target.label'),
      description: this._goalConfirmationDescription(),
      copy: this._goalConfirmationCopy(),
      confirmLabel: I18n.t('com.streamlain.ai.target.action.confirm'),
      cancelLabel: I18n.t('com.streamlain.ai.target.action.cancel'),
      settingsHint: null,
    };
    return html`<div
      class="chat-input-confirmation-card"
      data-testid="chat-goal-confirmation"
      @pointerdown=${(event: PointerEvent) => event.stopPropagation()}
    >
      <chat-message-confirmation
        .item=${item}
        .onRespond=${this._handleGoalConfirmationResponse}
      ></chat-message-confirmation>
    </div>`;
  }

  protected override render() {
    const { images, status } = this.chatContextValue;
    const pdfAttachments = this.chatContextValue.pdfAttachments || [];

    const input = html`<div
      class="chat-panel-input"
      data-independent-mode=${this.independentMode}
      data-if-focused=${this.focused}
      data-initial-border=${this.initialBorderPhase}
      @pointerdown=${this._handlePointerDown}
    >
      ${this._renderComposerTabRail()}
      ${this._renderMediaTray(images, pdfAttachments)}
      ${this._renderContextTray()}
      ${this.chatContextValue.quote
        ? html`<div
            class="chat-selection-quote"
            data-testid="chat-selection-quote"
          >
            ${repeat(
              getFirstTwoLines(this.chatContextValue.quote),
              line => line,
              line => html`<div>${line}</div>`
            )}
            <div
              class="chat-quote-close"
              @click=${() => {
                this.updateContext({ quote: '', markdown: '' });
              }}
            >
              ${CloseIcon()}
            </div>
          </div>`
        : nothing}
      ${this._renderTargetModeProposal()} ${this._renderGoalConfirmation()}
      ${this._renderToolConfirmation()}
      <textarea
        rows="1"
        ?autofocus=${this.autoFocusInput}
        placeholder=${this._inputPlaceholder}
        @input=${this._handleInput}
        @keydown=${this._handleKeyDown}
        @focus=${() => {
          this.focused = true;
        }}
        @blur=${() => {
          this.focused = false;
        }}
        @paste=${this._handlePaste}
        data-testid="chat-panel-input"
        ?hidden=${this._hasPendingDecision()}
      ></textarea>
      ${this.aiChatOnline
        ? nothing
        : html`<span
            class="chat-input-offline-status"
            role="status"
            aria-live="polite"
            aria-label=${this._sidebarOfflineBannerText}
            title=${this._sidebarOfflineBannerText}
            >${NoNetworkIcon({ width: '16px', height: '16px' })}</span
          >`}
      <div class="chat-panel-input-actions">
        <div class="chat-input-icon">
          <ai-chat-add-context
            .docId=${this.docId}
            .independentMode=${this.independentMode}
            .addChip=${this.addChip}
            .addImages=${this.addImages}
            .docDisplayConfig=${this.docDisplayConfig}
            .searchMenuConfig=${this.searchMenuConfig}
            .portalContainer=${this.portalContainer}
          ></ai-chat-add-context>
        </div>
        ${this._renderWebSearchButton()} ${this._renderAgentModeButton()}
        <chat-input-preference
          .session=${this.session}
          .extendedThinking=${this._isReasoningActive}
          .onExtendedThinkingChange=${this._toggleReasoning}
          .reasoningEffort=${this._selectedReasoningMode ?? 'medium'}
          .onReasoningEffortChange=${this._changeReasoningEffort}
          .aiModelService=${this.aiModelService}
          .keySource=${this.keySource}
          .availableInlineSize=${this.modelSelectorAvailableWidth}
          style=${this.modelSelectorAvailableWidth === null
            ? ''
            : `max-width:${this.modelSelectorAvailableWidth}px`}
        ></chat-input-preference>
        <div class="chat-input-right-controls">
          ${this._renderConfirmationProfileButton()}
          ${this._renderRecordingTime()} ${this._renderMicButton()}
          ${status === 'transmitting' || status === 'loading'
            ? html`<button
                class="chat-panel-stop"
                @click=${this._handleAbort}
                data-testid="chat-panel-stop"
              >
                ${ChatAbortIcon}
              </button>`
            : html`<button
                @click="${this._handleSendButtonClick}"
                class="chat-panel-send"
                aria-disabled=${this.isSendDisabled || !this.aiChatOnline}
                data-offline=${!this.aiChatOnline}
                title=${this.aiChatOnline ? '' : this._offlineAIMessage}
                data-testid="chat-panel-send"
              >
                ${ArrowUpBigIcon()}
              </button>`}
        </div>
      </div>
    </div>`;
    return input;
  }

  private async _sendTargetModeProposal(
    proposal: TargetModePermissionProposal,
    approved: boolean
  ) {
    if (this.pendingTargetModeProposal?.approvalId === proposal.approvalId) {
      this.pendingTargetModeProposal = null;
    }
    if (approved && proposal.includesAgentMode) {
      this._setAgentMode('agent');
    }
    this.skipNextTargetModeProposal = true;
    this.nextTargetRoutingDecision = proposal.includesTargetMode
      ? approved
        ? 'accepted'
        : 'declined'
      : null;
    this.aiToolsConfigService.setConfig({
      confirmationProfile: proposal.confirmationProfile,
    });
    this.textarea.value = proposal.input;
    this.isInputEmpty = !proposal.input.trim();
    this._syncTextareaHeight();
    await this._sendCurrentTextareaValue();
  }

  private readonly _handleTargetModeProposalResponse = async (
    approved: boolean
  ): Promise<ConfirmationMessage['toolResult']> => {
    const proposal = this.pendingTargetModeProposal;
    if (!proposal) return { success: false, error: 'Permission expired' };
    await this._persistTargetModeProposalResponse(proposal, approved);
    this._handleToolConfirmationResponded(proposal.approvalId);
    await this._sendTargetModeProposal(proposal, approved);
    return { success: true };
  };

  private readonly _handlePersistedTargetModeProposalResponse = async (
    confirmation: ConfirmationMessage,
    approved: boolean
  ): Promise<ConfirmationMessage['toolResult']> => {
    const proposal = restoreTargetModePermissionProposal(
      confirmation,
      this.session?.sessionId
    );
    if (!proposal) {
      return { success: false, error: 'Permission context unavailable' };
    }
    await this._persistTargetModeProposalResponse(proposal, approved);
    this._handleToolConfirmationResponded(proposal.approvalId);
    await this._sendTargetModeProposal(proposal, approved);
    return { success: true };
  };

  private readonly _handleConfirmationRequired = (
    detail: ConfirmationEventDetail
  ) => {
    const metadataSessionId =
      typeof detail.metadata?.sessionId === 'string'
        ? detail.metadata.sessionId
        : null;
    if (metadataSessionId && this.session?.sessionId !== metadataSessionId) {
      return;
    }
    this.pendingToolConfirmation = {
      type: 'confirmation',
      id: detail.id,
      toolName: detail.toolName,
      displayName: detail.displayName,
      description: detail.description,
      copy: detail.confirmationCopy,
      metadata: detail.metadata,
      settingsHint: null,
    };
    this.completedConfirmationIds = this.completedConfirmationIds.filter(
      id => id !== detail.id
    );
    this.updateContext({ status: 'idle' });
  };

  private _handleToolConfirmationResponded(confirmationId: string) {
    if (!this.completedConfirmationIds.includes(confirmationId)) {
      this.completedConfirmationIds = [
        ...this.completedConfirmationIds,
        confirmationId,
      ];
    }
    if (this.pendingToolConfirmation?.id === confirmationId) {
      this.pendingToolConfirmation = null;
    }
  }

  private get isSendDisabled() {
    return this.isInputEmpty;
  }

  private readonly _handlePointerDown = (e: MouseEvent) => {
    const target = e.target;
    if (
      target instanceof HTMLElement &&
      target.closest(
        '[data-chat-input-interactive], button, ai-chat-add-context, chat-input-preference, .chat-quote-close, .chat-input-confirmation-card'
      )
    ) {
      return;
    }
    if (target !== this.textarea) {
      // by default the div will be focused and will blur the textarea
      e.preventDefault();
      this.textarea.focus();
    }
  };

  private _getTextareaMaxHeight() {
    const viewportHeight = window.innerHeight || 800;
    const isCompact = window.matchMedia(
      '(max-width: 600px), (pointer: coarse)'
    ).matches;
    const viewportCap = Math.round(viewportHeight * (isCompact ? 0.26 : 0.3));
    return Math.max(88, Math.min(isCompact ? 180 : 260, viewportCap));
  }

  private _getTextareaSingleLineHeight() {
    const style = window.getComputedStyle(this.textarea);
    const fontSize = Number.parseFloat(style.fontSize);
    const lineHeight = Number.parseFloat(style.lineHeight);
    const resolvedLineHeight = Number.isFinite(lineHeight)
      ? lineHeight
      : fontSize * 1.2;
    const paddingBlock =
      Number.parseFloat(style.paddingTop) +
      Number.parseFloat(style.paddingBottom);

    return Math.max(22, Math.ceil(resolvedLineHeight + paddingBlock));
  }

  private _syncTextareaHeight() {
    const { textarea } = this;
    textarea.style.height = 'auto';
    const maxHeight = this._getTextareaMaxHeight();
    const singleLineHeight = this._getTextareaSingleLineHeight();
    // Browsers can report a larger scrollHeight for an empty textarea than
    // for its first real line. Ignore that unstable empty measurement and
    // reserve the exact same line box that one-line input uses.
    const contentHeight = textarea.value
      ? Math.max(textarea.scrollHeight, singleLineHeight)
      : singleLineHeight;
    const nextHeight = Math.min(contentHeight, maxHeight);
    textarea.style.height = `${nextHeight}px`;
    textarea.style.overflowY = contentHeight > maxHeight ? 'auto' : 'hidden';
  }

  private readonly _handleInput = async () => {
    const { textarea } = this;
    const value = textarea.value.trim();
    this.isInputEmpty = !value;
    if (
      this.pendingTargetModeProposal &&
      value !== this.pendingTargetModeProposal.input.trim()
    ) {
      this.pendingTargetModeProposal = null;
    }
    this._syncTextareaHeight();

    if (this.aiDraftService) {
      await this.aiDraftService.setDraft({
        input: value,
      });
    }
  };

  private readonly _handleKeyDown = async (evt: KeyboardEvent) => {
    if (evt.key === 'Enter' && !evt.shiftKey && !evt.isComposing) {
      await this._onTextareaSend(evt);
      return;
    }

    // Arrow-Up: Browse message history (when cursor at start)
    if (evt.key === 'ArrowUp' && this.textarea.selectionStart === 0) {
      this._navigateHistory('up');
      evt.preventDefault();
      return;
    }

    // Arrow-Down: Browse message history forward (when cursor at end)
    if (
      evt.key === 'ArrowDown' &&
      this.textarea.selectionStart === this.textarea.value.length
    ) {
      this._navigateHistory('down');
      evt.preventDefault();
    }
  };

  /**
   * Navigate through user message history with Arrow-Up/Down.
   * Like terminal/shell history navigation.
   */
  private readonly _navigateHistory = (direction: 'up' | 'down') => {
    // Get user-typed messages (exclude system-generated approval responses)
    const userMessages = this.chatContextValue.messages.filter(
      msg =>
        isChatMessage(msg) &&
        msg.role === 'user' &&
        (msg as ChatMessage & { messageType?: string }).messageType !==
          'tool_approval_response'
    );

    if (userMessages.length === 0) return;

    if (direction === 'up') {
      // Save current input when first entering history
      if (this.historyIndex === -1) {
        this._savedCurrentInput = this.textarea.value;
      }

      // Move up in history (towards older messages)
      const newIndex = Math.min(this.historyIndex + 1, userMessages.length - 1);
      if (newIndex !== this.historyIndex) {
        this.historyIndex = newIndex;
        // Get message from end (most recent first)
        const msg = userMessages[userMessages.length - 1 - newIndex];
        if (isChatMessage(msg)) {
          this.textarea.value = msg.content;
          this.isInputEmpty = !msg.content.trim();
          this._handleInput().catch(console.error);
        }
      }
    } else {
      // Move down in history (towards newer messages)
      const newIndex = this.historyIndex - 1;
      if (newIndex >= 0) {
        this.historyIndex = newIndex;
        const msg = userMessages[userMessages.length - 1 - newIndex];
        if (isChatMessage(msg)) {
          this.textarea.value = msg.content;
          this.isInputEmpty = !msg.content.trim();
          this._handleInput().catch(console.error);
        }
      } else if (newIndex === -1) {
        // Restore saved current input
        this.historyIndex = -1;
        this.textarea.value = this._savedCurrentInput;
        this.isInputEmpty = !this._savedCurrentInput.trim();
        this._handleInput().catch(console.error);
      }
    }
  };

  private readonly _handlePaste = async (event: ClipboardEvent) => {
    event.stopPropagation();
    const items = event.clipboardData?.items;

    if (!items) {
      return;
    }

    for (const index in items) {
      const item = items[index];

      // Handle images (existing)
      if (item.kind === 'file' && item.type.indexOf('image') >= 0) {
        event.preventDefault(); // Prevent browser from inserting filename as text
        const blob = item.getAsFile();
        if (!blob) continue;
        this.addImages([blob]);
        continue;
      }

      // sl-w252: Handle PDF paste
      if (item.kind === 'file' && item.type === 'application/pdf') {
        event.preventDefault();
        const file = item.getAsFile();
        if (!file) continue;
        this.addPdf(file).catch(console.error);
        continue;
      }

      // sl-57s: Handle BlockSuite snapshots (databases)
      if (item.type === 'BLOCKSUITE/SNAPSHOT') {
        try {
          const data = await new Promise<string>(resolve => {
            item.getAsString(resolve);
          });
          const snapshotData = this._tryParseDatabaseFromClipboard(data);
          if (snapshotData) {
            this._addBlocksFromClipboard(snapshotData);
          }
        } catch (error) {
          console.warn('[sl-57s] Failed to parse BLOCKSUITE/SNAPSHOT:', error);
        }
        continue;
      }

      // sl-57s: Fallback - try text/plain for database detection
      if (item.type === 'text/plain') {
        try {
          const text = await new Promise<string>(resolve => {
            item.getAsString(resolve);
          });

          // Try to parse as BlockSuite snapshot
          const snapshotData = this._tryParseDatabaseFromClipboard(text);
          if (snapshotData) {
            this._addBlocksFromClipboard(snapshotData);
          }
        } catch (error) {
          console.warn('[sl-57s] Failed to parse clipboard text:', error);
        }
      }

      // sl-57s: Also check text/html
      if (item.type === 'text/html') {
        try {
          const html = await new Promise<string>(resolve => {
            item.getAsString(resolve);
          });

          // Extract BlockSuite snapshot from data-blocksuite-snapshot attribute
          const snapshotMatch = html.match(
            /data-blocksuite-snapshot="([^"]+)"/
          );
          if (snapshotMatch) {
            try {
              // Decompress using lz-string (BlockSuite uses compressToEncodedURIComponent)
              const compressedData = snapshotMatch[1];
              const decompressed =
                lz.decompressFromEncodedURIComponent(compressedData);

              if (!decompressed) {
                console.warn(
                  '[sl-57s] Failed to decompress snapshot - got null/undefined'
                );
                continue;
              }

              const snapshotData =
                this._tryParseDatabaseFromClipboard(decompressed);
              if (snapshotData) {
                this._addBlocksFromClipboard(snapshotData);
              }
            } catch (error) {
              console.warn(
                '[sl-57s] Failed to decompress/parse snapshot:',
                error
              );
            }
          }
        } catch (error) {
          console.warn('[sl-57s] Failed to parse HTML:', error);
        }
      }
    }
  };

  /**
   * sl-57s: Try to parse database data from clipboard text
   * Detects BLOCKSUITE/SNAPSHOT format and extracts database blocks
   */
  private _tryParseDatabaseFromClipboard(text: string): any | null {
    try {
      const parsed = JSON.parse(text);

      // BlockSuite clipboard format has multiple MIME types
      // The actual snapshot is in 'blocksuite/surface' as a JSON string
      if (parsed['blocksuite/surface']) {
        const surfaceData = JSON.parse(parsed['blocksuite/surface']);

        // The surface data contains a snapshot array
        if (Array.isArray(surfaceData.snapshot)) {
          return { snapshot: { content: surfaceData.snapshot } };
        }
      }

      // Fallback: check if it's a direct BlockSuite snapshot
      if (
        parsed.snapshot?.type === 'slice' &&
        Array.isArray(parsed.snapshot.content)
      ) {
        return parsed;
      }
    } catch (error) {
      console.warn('[sl-57s] Failed to parse clipboard JSON:', error);
    }

    return null;
  }

  /**
   * sl-57s: Extract database blocks from BlockSuite snapshot
   */
  private _extractDatabasesFromSnapshot(
    snapshotData: any
  ): DatabaseAttachment[] {
    const databases: DatabaseAttachment[] = [];
    const snapshot = snapshotData.snapshot;

    if (!snapshot?.content) return databases;

    for (const block of snapshot.content) {
      if (block.flavour === 'affine:database') {
        databases.push({
          docId: this.docId,
          blockId: block.id,
          name: this._extractTitle(block.props?.title),
          columns: this._extractColumns(block.props?.columns),
          bounds: finiteBoundsFromXYWH(block.xywh ?? block.props?.xywh),
          source: 'paste',
          priority: 'highest',
          scope: 'metadata-only',
        });
      }
    }

    return databases;
  }

  /**
   * sl-57s: Extract title from YCRDT Text format
   */
  private _extractTitle(titleProp: any): string {
    // Handle YCRDT Text format with delta
    if (titleProp?.delta && Array.isArray(titleProp.delta)) {
      const text = titleProp.delta
        .map((d: any) => d.insert || '')
        .join('')
        .trim();
      if (text) return text;
    }

    // Fallback: try toString
    if (titleProp?.toString) {
      const text = titleProp.toString().trim();
      if (text) return text;
    }

    return 'Untitled Database';
  }

  /**
   * sl-57s: Extract columns from database props
   */
  private _extractColumns(columnsProp: any): DatabaseColumn[] {
    if (!Array.isArray(columnsProp)) return [];

    return columnsProp.map(col => ({
      id: col.id || '',
      name: col.name || col.id || 'Untitled',
      type: col.type || 'unknown',
    }));
  }

  /**
   * Add database and automation attachments from clipboard data
   */
  private _addBlocksFromClipboard(snapshotData: any): void {
    const databases = this._extractDatabasesFromSnapshot(snapshotData);
    const automations = this._extractAutomationsFromSnapshot(snapshotData);

    if (databases.length > 0) {
      const currentDb = this.chatContextValue.databaseAttachments || [];
      this.updateContext({
        databaseAttachments: [...currentDb, ...databases],
      });
    }

    if (automations.length > 0) {
      const currentAuto = this.chatContextValue.automationAttachments || [];
      this.updateContext({
        automationAttachments: [...currentAuto, ...automations],
      });
    }
  }

  /**
   * Extract automation blocks from BlockSuite snapshot
   */
  private _extractAutomationsFromSnapshot(
    snapshotData: any
  ): AutomationAttachment[] {
    const automations: AutomationAttachment[] = [];
    const snapshot = snapshotData.snapshot;

    if (!snapshot?.content) return automations;

    for (const block of snapshot.content) {
      if (block.flavour === 'affine:automation') {
        automations.push({
          docId: this.docId,
          blockId: block.id,
          name: this._extractTitle(block.props?.title) || 'Untitled Automation',
          bounds: finiteBoundsFromXYWH(block.xywh ?? block.props?.xywh),
          sourceTableId: block.props?.sourceTableId ?? null,
          sourceTableName: block.props?.sourceTableName ?? null,
          workflowId: block.props?.workflowId ?? null,
          source: 'paste',
          priority: 'highest',
        });
      }
    }

    return automations;
  }

  private readonly _handleAbort = () => {
    this.chatContextValue.abortController?.abort();
    this.updateContext({ status: 'success' });
    reportResponse('aborted:stop');
  };

  private readonly _toggleReasoning = (extendedThinking: boolean) => {
    this.reasoningConfig.setEnabled(extendedThinking);
  };

  private readonly _changeReasoningEffort = (effort: ReasoningModeId) => {
    this.reasoningConfig.setEffort(effort);
  };

  private readonly _fetchKeySource = async () => {
    try {
      const data = await fetchChatKeysCheck(this.workspaceId);
      this.keySource = data.source;
    } catch {
      // Silent — hint is non-critical
    }
  };

  /**
   * Sync an initial effective model only when the local sidebar selection has
   * not already restored one from GlobalState.
   */
  private readonly _syncModelPreference = async () => {
    if (!this.workspaceId) {
      return;
    }
    await this.aiModelService.syncChatModelPreferenceForWorkspace(
      this.workspaceId
    );
  };

  private readonly _syncModelPreferenceFromSettings = async () => {
    if (!this.workspaceId) {
      return;
    }
    await this.aiModelService.syncChatModelPreferenceForWorkspace(
      this.workspaceId,
      { overwriteLocal: true }
    );
  };

  // ═══════════════════════════════════════════════════════════════════════════
  // E020-S007: Speech-to-Text Recording Methods
  // ═══════════════════════════════════════════════════════════════════════════

  private readonly _handleMicClick = (event: MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();
    if (this.isRecording) {
      this._stopRecording();
      return;
    }
    this._startRecording().catch(console.error);
  };

  private readonly _startRecording = async () => {
    if (this.isRecording || this.isTranscribing) {
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });

      // Prefer webm, fallback to mp4 for Safari
      const mimeType = MediaRecorder.isTypeSupported('audio/webm')
        ? 'audio/webm'
        : 'audio/mp4';

      this.mediaRecorder = new MediaRecorder(stream, { mimeType });
      this.audioChunks = [];
      this.recordingSeconds = 0;
      this.recordingStartedAtMs = null;

      this.mediaRecorder.ondataavailable = e => {
        if (e.data.size > 0) {
          this.audioChunks.push(e.data);
        }
      };

      this.mediaRecorder.onstop = () => {
        stream.getTracks().forEach(t => t.stop());
        this._clearRecordingTimer();
        this._transcribeAudio().catch(console.error);
      };

      this.mediaRecorder.start();
      this.isRecording = true;
      this.recordingStartedAtMs = performance.now();

      // Start timer
      this.recordingTimer = window.setInterval(() => {
        this.recordingSeconds++;
        if (this.recordingSeconds >= MAX_RECORDING_SECONDS) {
          this._stopRecording(); // Auto-send to transcription at limit.
        }
      }, 1000);
    } catch (err) {
      console.error('Failed to start recording:', err);
      this.notificationService?.notify({
        title: I18n.t('com.streamlain.ai.chat.input.microphone-denied.title'),
        message: I18n.t(
          'com.streamlain.ai.chat.input.microphone-denied.message'
        ),
        accent: 'error',
      });
    }
  };

  private readonly _stopRecording = () => {
    if (this.mediaRecorder?.state === 'recording') {
      this.mediaRecorder.stop();
      this.isRecording = false;
    }
  };

  private readonly _clearRecordingTimer = () => {
    if (this.recordingTimer) {
      clearInterval(this.recordingTimer);
      this.recordingTimer = null;
    }
  };

  private readonly _transcribeAudio = async () => {
    this.isTranscribing = true;
    try {
      const durationMs =
        this.recordingStartedAtMs === null
          ? this.recordingSeconds * 1000
          : performance.now() - this.recordingStartedAtMs;
      const mimeType = this.audioChunks[0]?.type || 'audio/webm';
      const blob = new Blob(this.audioChunks, { type: mimeType });
      if (blob.size === 0 || durationMs < MIN_TRANSCRIPTION_RECORDING_MS) {
        return;
      }
      const formData = new FormData();
      formData.append('audio', blob, 'recording.webm');
      formData.append(
        'durationMs',
        String(Math.max(1, Math.round(durationMs)))
      );

      // sl-y41p: Include workspaceId for 3-level key hierarchy
      const url = new URL(
        '/api/copilot/user-keys/transcribe-chat',
        window.location.origin
      );
      url.searchParams.set('workspaceId', this.workspaceId);

      const response = await workspaceApiFetch(
        url.toString(),
        {
          method: 'POST',
          body: formData,
          credentials: 'include',
        },
        {
          workspaceId: this.workspaceId,
          category: 'foreground-action',
          mode: 'foreground',
          priority: 'foreground',
        }
      );

      if (response.ok) {
        const { text } = (await response.json()) as { text: string };
        // Append to existing text or set new text
        const currentValue = this.textarea.value.trim();
        this.textarea.value = currentValue ? `${currentValue} ${text}` : text;
        this.isInputEmpty = false;
        this._handleInput().catch(console.error);
      } else {
        const error = (await response.json().catch(() => ({}))) as {
          message?: string;
        };
        throw new Error(
          error.message ||
            I18n.t('com.streamlain.ai.chat.input.transcription.failed')
        );
      }
    } catch (err) {
      console.error('Transcription failed:', err);
      this.notificationService?.notify({
        title: I18n.t('com.streamlain.ai.chat.input.transcription.failed'),
        message:
          err instanceof Error
            ? err.message
            : I18n.t(
                'com.streamlain.ai.chat.input.transcription.failed-message'
              ),
        accent: 'error',
      });
    } finally {
      this.isTranscribing = false;
      this.recordingSeconds = 0;
      this.recordingStartedAtMs = null;
    }
  };

  private _formatRecordingTime(seconds: number): string {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  }

  private _renderMicButton() {
    // Check if MediaRecorder is supported
    if (typeof MediaRecorder === 'undefined') {
      return nothing;
    }

    if (this.isRecording) {
      return html`
        <button
          @click=${this._handleMicClick}
          class="chat-panel-mic recording"
          title=${I18n.t('com.streamlain.ai.chat.input.voice.send')}
        >
          ${StopIcon()}
        </button>
      `;
    }

    if (this.isTranscribing) {
      return html`
        <button
          class="chat-panel-mic transcribing"
          aria-disabled="true"
          title=${I18n.t('com.streamlain.ai.chat.input.voice.transcribing')}
        >
          <svg
            class="spinner-icon"
            width="1em"
            height="1em"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="1.8"
          >
            <circle cx="12" cy="12" r="10" stroke-opacity="0.25" />
            <path d="M12 2a10 10 0 0 1 10 10" stroke-linecap="round" />
          </svg>
        </button>
      `;
    }

    return html`
      <button
        @click=${this._handleMicClick}
        class="chat-panel-mic"
        title=${I18n.t('com.streamlain.ai.chat.input.voice.start')}
      >
        ${MicrophoneIcon()}
      </button>
    `;
  }

  private _renderRecordingTime() {
    if (!this.isRecording) {
      return nothing;
    }
    return html`
      <span class="recording-time"
        >${this._formatRecordingTime(this.recordingSeconds)}</span
      >
    `;
  }

  private readonly _handleImageRemove = (index: number) => {
    const oldImages = this.chatContextValue.images;
    const newImages = oldImages.filter((_, i) => i !== index);
    this.updateContext({ images: newImages });
  };

  private readonly _handleSendButtonClick = (e: MouseEvent | KeyboardEvent) => {
    if (!this.aiChatOnline) {
      e.preventDefault();
      e.stopPropagation();
      this._showOfflineAIToast();
      return;
    }

    void this._onTextareaSend(e);
  };

  private async _shouldShowTargetModeProposal(
    rawInput: string,
    normalizedInput: string
  ): Promise<boolean> {
    if (this.skipNextTargetModeProposal) {
      this.skipNextTargetModeProposal = false;
      return false;
    }
    if (this.pendingTargetModeProposal || this.targetModeProposalInFlight) {
      return true;
    }

    const proposal = resolveTargetModeProposal(
      normalizedInput,
      this._agentMode,
      {
        hasAttachedAutomation:
          (this.chatContextValue.automationAttachments?.length ?? 0) > 0,
        hasAttachedDatabase:
          (this.chatContextValue.databaseAttachments?.length ?? 0) > 0,
      }
    );
    if (!proposal) return false;
    const includesAgentMode =
      this._agentMode === 'chat' && shouldProposeAgentMode(normalizedInput);
    const includesTargetMode = this.targetModeEnabled;
    if (!includesAgentMode && !includesTargetMode) return false;
    const recommendedConfirmationProfile = includesTargetMode
      ? 'autonomous'
      : this._confirmationProfile;
    this.targetModeProposalInFlight = true;
    try {
      const sessionId = (await this.createSession())?.sessionId;
      if (!sessionId) throw new Error('Unable to create chat session');
      const proposalCopy = {
        key: includesAgentMode
          ? includesTargetMode
            ? proposal.domain === 'automation'
              ? 'com.streamlain.ai.chat.input.target.proposal.description.combined.automation'
              : 'com.streamlain.ai.chat.input.target.proposal.description.combined.app'
            : proposal.domain === 'automation'
              ? 'com.streamlain.ai.chat.input.target.proposal.description.agent.automation'
              : 'com.streamlain.ai.chat.input.target.proposal.description.agent.app'
          : proposal.domain === 'automation'
            ? 'com.streamlain.ai.chat.input.target.proposal.description.target.automation'
            : 'com.streamlain.ai.chat.input.target.proposal.description.target.app',
      } as const;
      const pending: TargetModePermissionProposal = {
        input: rawInput,
        domain: proposal.domain,
        reason: proposal.reason,
        approvalId: `${includesAgentMode ? 'agent' : 'target'}-mode-proposal:${crypto.randomUUID()}`,
        sessionId,
        confirmationProfile: recommendedConfirmationProfile,
        description: I18n.t(proposalCopy.key),
        copy: proposalCopy,
        includesAgentMode,
        includesTargetMode,
      };
      this.pendingTargetModeProposal = pending;
      try {
        const message = await persistTargetModePermissionRequest(
          pending,
          this.workspaceId,
          this.docId,
          this.serverService.server.fetch
        );
        this._appendPermissionHistoryMessage(message);
      } catch (error) {
        if (this.pendingTargetModeProposal?.approvalId === pending.approvalId) {
          this.pendingTargetModeProposal = null;
        }
        throw error;
      }
      return true;
    } finally {
      this.targetModeProposalInFlight = false;
    }
  }

  private async _persistTargetModeProposalResponse(
    proposal: TargetModePermissionProposal,
    approved: boolean
  ) {
    const message = await persistTargetModePermissionResponse(
      proposal,
      approved,
      this.workspaceId,
      this.docId,
      this.serverService.server.fetch
    );
    this._appendPermissionHistoryMessage(message);
  }

  private _appendPermissionHistoryMessage(message: ChatMessage) {
    if (
      message.id &&
      this.chatContextValue.messages.some(
        item => isChatMessage(item) && item.id === message.id
      )
    ) {
      return;
    }
    this.updateContext({
      messages: [...this.chatContextValue.messages, message],
    });
  }

  private async _sendCurrentTextareaValue() {
    const rawValue = this.textarea.value.trim();
    const agentMention = resolveLeadingChatAgentMention(rawValue, this.bots);
    const value = agentMention?.input ?? rawValue;
    if (value.length === 0) {
      if (agentMention) {
        this.onSelectBot?.(agentMention.botId);
        this.textarea.value = '';
        this.isInputEmpty = true;
        if (this.aiDraftService) {
          await this.aiDraftService.setDraft({ input: '' });
        }
      }
      return;
    }

    // Guard: Prevent double-send (race condition)
    const status = this.chatContextValue.status;
    if (status === 'loading' || status === 'transmitting') {
      return;
    }

    if (!this.aiChatOnline) {
      this._showOfflineAIToast();
      return;
    }

    if (await this._shouldShowTargetModeProposal(rawValue, value)) {
      return;
    }

    if (agentMention) {
      this.onSelectBot?.(agentMention.botId);
    }

    this.textarea.value = '';
    this.isInputEmpty = true;
    this.textarea.style.height = '22px';
    this.textarea.style.overflowY = 'hidden';

    // Reset history navigation
    this.historyIndex = -1;
    this._savedCurrentInput = '';

    if (this.aiDraftService) {
      await this.aiDraftService.setDraft({
        input: '',
      });
    }
    await this.send(value);
  }

  private readonly _onTextareaSend = async (e: MouseEvent | KeyboardEvent) => {
    e.preventDefault();
    e.stopPropagation();
    try {
      await this._sendCurrentTextareaValue();
    } catch (error) {
      this.updateContext({ status: 'error', error: error as AIError });
    }
  };

  // E010-T015: Get viewport center for Edgeless canvas
  private getViewportCenter(): { x: number; y: number } | null {
    try {
      const gfx = this.host?.std?.get(GfxControllerIdentifier);
      if (gfx?.viewport?.center) {
        return gfx.viewport.center;
      }
    } catch {
      // Not in Edgeless mode or viewport not available
    }
    return null;
  }

  private async flushActiveDocBeforeChat(): Promise<void> {
    const docId = this.docId;
    if (!docId) {
      return;
    }

    const docEngine = (
      this.host?.store.workspace as
        | {
            engine?: {
              doc?: {
                addPriority?: (docId: string, priority: number) => void;
                flushAndWaitForSynced?: (docId: string) => Promise<void>;
              };
            };
          }
        | undefined
    )?.engine?.doc;

    if (!docEngine?.flushAndWaitForSynced) {
      return;
    }

    docEngine.addPriority?.(docId, 100);
    await docEngine.flushAndWaitForSynced(docId);
  }

  send = async (text: string) => {
    try {
      const {
        status,
        markdown,
        images,
        snapshot,
        combinedElementsMarkdown,
        html,
      } = this.chatContextValue;

      if (status === 'loading' || status === 'transmitting') return;
      if (!text) return;
      if (!this.aiChatOnline) {
        this._showOfflineAIToast();
        return;
      }
      if (!AIProvider.actions.chat) return;

      const abortController = new AbortController();
      // Keep attachments for sending, will be cleared after successful send
      const currentDatabaseAttachments =
        this.chatContextValue.databaseAttachments || [];
      const currentAutomationAttachments =
        this.chatContextValue.automationAttachments || [];
      const currentWorkflowAttachments =
        this.chatContextValue.workflowAttachments || [];
      const currentCodeSelections = this.chatContextValue.codeSelections || [];
      const currentMailAttachments =
        this.chatContextValue.mailAttachments || [];
      // sl-w252: Capture PDF files before clearing context
      const currentPdfAttachments = this.chatContextValue.pdfAttachments || [];
      const currentPdfContextAttachments =
        this.chatContextValue.pdfContextAttachments || [];
      const explicitCanvasSelections =
        this.chatContextValue.canvasSelections || [];
      const hiddenCanvasContextBlockIds = [
        ...currentDatabaseAttachments.map(attachment => attachment.blockId),
        ...currentAutomationAttachments.map(attachment => attachment.blockId),
        ...currentPdfContextAttachments.map(attachment => attachment.blockId),
        ...this.excludedCanvasSelectionBlockIds,
      ];
      const implicitCanvasSelections = this.host
        ? filterCanvasSelectionsByCoveredBlockIds(
            extractCanvasSelectionContext(this.host),
            hiddenCanvasContextBlockIds,
            this.canvasAIChatContextMode
          )
        : [];
      const currentCanvasSelections = filterCanvasSelectionsByCoveredBlockIds(
        mergeCanvasSelections(
          explicitCanvasSelections,
          implicitCanvasSelections
        ),
        hiddenCanvasContextBlockIds,
        this.canvasAIChatContextMode
      );
      const selectedContext =
        this.host && currentCanvasSelections.length
          ? await extractSelectedContent(this.host)
          : null;
      const selectedImages = selectedContext?.images ?? [];
      const selectedSnapshot = selectedContext?.snapshot ?? snapshot;
      const selectedMarkdown =
        selectedContext?.combinedElementsMarkdown ?? combinedElementsMarkdown;
      const selectedCanvasSelections = filterCanvasSelectionsByCoveredBlockIds(
        mergeCanvasSelections(
          currentCanvasSelections,
          selectedContext?.canvasSelections ?? []
        ),
        hiddenCanvasContextBlockIds,
        this.canvasAIChatContextMode
      );
      this.updateContext({
        images: [],
        pdfAttachments: [], // sl-w252: Clear PDFs on send
        canvasSelections: selectedCanvasSelections,
        status: 'loading',
        error: null,
        quote: '',
        markdown: '',
        abortController,
      });

      const sendImages = [...(images ?? []), ...selectedImages];
      const imageAttachments = await Promise.all(
        sendImages.map(image => readBlobAsURL(image))
      );
      // sl-w252: Include PDF thumbnail data URLs for chat history display
      const pdfThumbnailEntries = currentPdfAttachments.flatMap(
        (pdf, pdfIndex) =>
          pdf.thumbnailDataUrl
            ? [
                {
                  pdfIndex,
                  file: dataUrlToFile(
                    pdf.thumbnailDataUrl,
                    `${pdf.name}.preview.jpg`,
                    'image/jpeg'
                  ),
                },
              ]
            : []
      );
      const pdfThumbnailFiles = pdfThumbnailEntries.map(entry => entry.file);
      const pdfThumbnailUploadIndexByPdf = new Map<number, number>();
      pdfThumbnailEntries.forEach((entry, thumbnailIndex) => {
        pdfThumbnailUploadIndexByPdf.set(
          entry.pdfIndex,
          sendImages.length + thumbnailIndex
        );
      });
      const pdfUploadIndexOffset = sendImages.length + pdfThumbnailFiles.length;
      const pdfThumbnailUrls = currentPdfAttachments
        .map(p => p.thumbnailDataUrl)
        .filter((url): url is string => url !== null);
      const allVisualAttachments = [...imageAttachments, ...pdfThumbnailUrls];
      const imageContextAttachments = sendImages.map((image, index) => ({
        id: `image:local:${index}:${image.name || 'image'}`,
        fileName: image.name || `image-${index + 1}`,
        uploadIndex: index,
        preview: imageAttachments[index] ?? null,
        mime: image.type || null,
        size: image.size || null,
        source: 'paste',
        priority: 'mid',
      }));
      const pdfFileContextAttachments = currentPdfAttachments.map(
        (pdf, index) => ({
          id: `pdf-file:local:${pdf.name}`,
          fileName: pdf.name,
          uploadIndex: pdfUploadIndexOffset + index,
          thumbnailUploadIndex: pdfThumbnailUploadIndexByPdf.get(index),
          thumbnailPreview: pdf.thumbnailDataUrl,
          mime: pdf.file.type || 'application/pdf',
          size: pdf.file.size || null,
          pageCount: pdf.pageCount,
          source: pdf.source,
          priority: 'mid',
        })
      );
      const fileContextAttachments = fileContextAttachmentsFromChips(
        this.chips.filter(isFileChip)
      );
      const attachmentContextRefs = attachmentContextRefsFromChips(
        this.chips.filter(isAttachmentChip)
      );
      const tagContexts = this.chips.filter(isTagChip).map(chip => ({
        tagId: chip.tagId,
        title: this.docDisplayConfig.getTagTitle(chip.tagId),
        docIds: this.docDisplayConfig.getTagPageIds(chip.tagId),
        source: 'focus',
        priority: 'mid',
      }));
      const collections = this.docDisplayConfig.getCollections();
      const collectionTitleById = new Map(
        collections.signal.value.map(collection => [
          collection.id,
          collection.name,
        ])
      );
      collections.cleanup();
      const collectionContexts = this.chips
        .filter(isCollectionChip)
        .map(chip => ({
          collectionId: chip.collectionId,
          title:
            collectionTitleById.get(chip.collectionId) ?? chip.collectionId,
          docIds: this.docDisplayConfig.getCollectionPageIds(chip.collectionId),
          source: 'focus',
          priority: 'mid',
        }));
      const userInput = (markdown ? `${markdown}\n` : '') + text;
      const retrievalPlan = resolveCopilotRetrievalPlan(
        userInput,
        this.aiToolsConfigService.config.value,
        !!this.networkSearchConfig.visible.value
      );
      const contexts = await this._getMatchedContexts();
      if (abortController.signal.aborted) {
        return;
      }

      const canonicalContext = buildCanonicalMessageContext({
        imageAttachments: imageContextAttachments,
        pdfFileAttachments: pdfFileContextAttachments,
        fileAttachments: fileContextAttachments,
        attachmentRefs: attachmentContextRefs,
        databaseAttachments: currentDatabaseAttachments,
        automationAttachments: currentAutomationAttachments,
        workflowAttachments: currentWorkflowAttachments,
        codeSelections: currentCodeSelections,
        pdfContextAttachments: currentPdfContextAttachments,
        canvasSelections: selectedCanvasSelections,
        mailAttachments: currentMailAttachments,
        docContexts: contexts.docs,
        tagContexts,
        collectionContexts,
        docId: this.docId,
        searchMode: retrievalPlan.webSearch ? SearchMode.WEB : SearchMode.AUTO,
      });

      // optimistic update messages
      await this._preUpdateMessages(
        userInput,
        allVisualAttachments,
        canonicalContext
      );
      await this.flushActiveDocBeforeChat();

      const sessionId = (await this.createSession())?.sessionId;
      await this.onChatStart?.();

      const enableSendDetailedObject =
        this.affineFeatureFlagService.flags.enable_send_detailed_object_to_ai
          .value;

      const modelId = this.aiModelService.modelId.value;
      // E010-T015: Get viewport for Edgeless canvas positioning
      const viewport = this.getViewportCenter();
      const targetRoutingDecision = this.nextTargetRoutingDecision;
      this.nextTargetRoutingDecision = null;

      const stream = await AIProvider.actions.chat({
        sessionId,
        input: userInput,
        contexts: {
          ...contexts,
          selectedSnapshot:
            selectedSnapshot && enableSendDetailedObject
              ? selectedSnapshot
              : undefined,
          selectedMarkdown:
            selectedMarkdown && enableSendDetailedObject
              ? selectedMarkdown
              : undefined,
          html: html || undefined,
        },
        docId: this.docId,
        // sl-w252: Send images + PDF thumbnails (for chat display after reload) + PDF files (for AI vision)
        attachments: [
          ...sendImages,
          ...pdfThumbnailFiles,
          ...currentPdfAttachments.map(p => p.file),
        ],
        context: canonicalContext,
        workspaceId: this.workspaceId,
        stream: true,
        signal: abortController.signal,
        isRootSession: this.isRootSession,
        where: this.trackOptions?.where,
        control: this.trackOptions?.control,
        webSearch: retrievalPlan.webSearch,
        reasoning: this._isReasoningActive,
        reasoningEffort: this._selectedReasoningMode,
        toolsConfig: {
          ...retrievalPlan.toolsConfig,
          targetMode: this.targetModeEnabled ? 'enabled' : 'disabled',
          ...(targetRoutingDecision ? { targetRoutingDecision } : {}),
        },
        modelId,
        // E010-T015: Pass viewport to backend for MCP tools
        viewportX: viewport?.x,
        viewportY: viewport?.y,
      });

      for await (const text of stream) {
        const messages = this.chatContextValue.messages.slice(0);
        const last = messages.at(-1);
        if (last && isChatMessage(last)) {
          try {
            const parsed = StreamObjectSchema.parse(JSON.parse(text));
            forwardCopilotWorkflowStreamObject(parsed);
            const streamObjects = mergeStreamObjects([
              ...(last.streamObjects ?? []),
              parsed,
            ]);
            messages[messages.length - 1] = {
              ...last,
              streamObjects,
            };
          } catch {
            if (last.streamObjects?.length) {
              // When streamObjects exist (e.g. reasoning blocks), raw text
              // must join them as text-delta — otherwise renderContent()
              // only renders streamObjects and the text content is lost.
              const streamObjects = mergeStreamObjects([
                ...(last.streamObjects ?? []),
                { type: 'text-delta' as const, textDelta: text },
              ]);
              messages[messages.length - 1] = {
                ...last,
                streamObjects,
              };
            } else {
              messages[messages.length - 1] = {
                ...last,
                content: last.content + text,
              };
            }
          }
          this.updateContext({ messages, status: 'transmitting' });
        }
      }

      // sl-z4tb: Keep UI-scoped attachments alive. Database and workflow
      // attachments represent editors that are still visibly open. Clearing
      // them after a successful turn strips the current request authority from
      // follow-ups even though the user never left that editor. Their owners
      // remove them when the editor closes or the user dismisses the chip.
      this.updateContext({
        status: 'success',
        automationAttachments: [],
      });
      // update message id from server
      await this._postUpdateMessages(sessionId);
      await this.onChatSuccess?.();
    } catch (error) {
      this.updateContext({ status: 'error', error: error as AIError });
      await this.onChatError?.(error);
    } finally {
      this.updateContext({ abortController: null });
    }
  };

  private readonly _preUpdateMessages = async (
    userInput: string,
    attachments: string[],
    context?: ChatMessage['context']
  ) => {
    const userInfo = await AIProvider.userInfo;
    this.updateContext({
      messages: [
        ...this.chatContextValue.messages,
        {
          id: '',
          role: 'user',
          content: userInput,
          createdAt: new Date().toISOString(),
          attachments,
          context,
          userId: userInfo?.id,
          userName: userInfo?.name,
          avatarUrl: userInfo?.avatarUrl ?? undefined,
        },
        {
          id: '',
          role: 'assistant',
          content: '',
          createdAt: new Date().toISOString(),
        },
      ],
    });
  };

  private readonly _postUpdateMessages = async (sessionId?: string) => {
    if (!sessionId || !AIProvider.histories) return;

    const { messages } = this.chatContextValue;
    const historyIds = await AIProvider.histories.ids(
      this.workspaceId,
      this.docId,
      { sessionId, withMessages: true }
    );
    const persistedMessages = historyIds?.[0]?.messages ?? [];
    const nextMessages = syncMissingMessageIds(messages, persistedMessages);
    if (!nextMessages) return;

    this.updateContext({ messages: nextMessages });
  };

  private async _getMatchedContexts() {
    const docContexts = new Map<
      string,
      { docId: string; docContent: string }
    >();

    this.chips.forEach(chip => {
      if (isDocChip(chip) && !!chip.markdown?.value) {
        docContexts.set(chip.docId, {
          docId: chip.docId,
          docContent: chip.markdown.value,
        });
      }
    });

    const docs: BlockSuitePresets.AIDocContextOption[] =
      this.externalContextDocs.map(doc => ({
        docId: doc.docId,
        docContent: doc.docContent,
        docTitle: doc.docTitle ?? doc.docId,
        tags: doc.tags ?? '',
        createDate: doc.createDate ?? '',
        updatedDate: doc.updatedDate ?? '',
      }));

    docs.push(
      ...Array.from(docContexts.values()).map(doc => {
        const docMeta = this.docDisplayConfig.getDocMeta(doc.docId);
        const docTitle = this.docDisplayConfig.getTitle(doc.docId);
        const tags = docMeta?.tags
          ? docMeta.tags
              .map(tagId => this.docDisplayConfig.getTagTitle(tagId))
              .join(',')
          : '';
        return {
          docId: doc.docId,
          docContent: doc.docContent,
          docTitle,
          tags,
          createDate: docMeta?.createDate
            ? new Date(docMeta.createDate).toISOString()
            : '',
          updatedDate: docMeta?.updatedDate
            ? new Date(docMeta.updatedDate).toISOString()
            : '',
        };
      })
    );

    return { docs, files: [] };
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'ai-chat-input': AIChatInput;
  }
}
